import usePageFilterOptions from "../hooks/usePageFilterOptions";
import ContentEditorFields from "../components/content/ContentEditorFields";
import ContentDetailsView, { CONTENT_DETAIL_TABS } from "../components/content/ContentDetailsView";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CONTENT_EXPORT_TAB_FIELDS } from "../utils/exportTabFields";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthController from "../hooks/useAuthController";
import useContentRouteTab from "../hooks/useContentRouteTab";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  TableCell,
  TextField,
  Typography,
} from "@mui/material";
import { FilterListRounded as FilterListRoundedIcon } from "../components/icons/FigmaControlIcons";
import Breadcrumb from "../components/Breadcrumb";
import GenericDataTable from "../components/GenericDataTable";
import BulkActionsBar from "../components/BulkActionsBar";
import AssignTeamMemberAction from "../components/AssignTeamMemberAction";
import PopupMultiSelectField, {
  popupFilterInputSx,
} from "../components/PopupMultiSelectField";
import StatsSlider from "../components/StatsSlider";
import TabNavigationBar from "../components/TabNavigationBar";
import TopListHeaderBar from "../components/TopListHeaderBar";
import ListTableTabsTemplate from "../components/layouts/ListTableTabsTemplate";
import ReusableRightDrawer from "../components/ReusableRightDrawer";
import { AgentActionIcon } from "../components/ActionButtonIcons";
import AddJobDialog from "../components/PopupWindows/AddJobDialog.jsx";
import AddToSequenceDialog from "../components/PopupWindows/AddToSequenceDialog.jsx";
import CsvImportDialog from "../components/CsvImportDialog";
import useCsvImport from "../hooks/useCsvImport";
import { sanitizeImportedSourceText } from "../utils/importTextSanitizers";
import { buildJobSequenceItem } from "../utils/sequenceItems";
import { toolbarPillButtonSx } from "../utils/pillButtonStyles";
import {
  createSequence,
  fetchSequences,
  fetchSequenceById,
  getSequenceEntityLabel,
} from "../utils/sequences";
import {
  CONTENT_COLUMNS,
  CONTENT_TABS,
  CONTENT_USAGE_STAT_OPTIONS,
  DEFAULT_CONTENT_USAGE_STAT_OPTION_ID,
  STATUS_OPTIONS,
  STATUS_STYLES,
  createInitialRowsByTab,
  createJobRowFromApi,
  getContentStatTargetTabId,
  getPrimaryContentStatIdForTab,
  matchesContentStatFilter,
  createTopStats,
} from "../components/content/contentData";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { Eye, FilterSearch } from "iconsax-react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { findTabLabel } from "../utils/breadcrumbs";
import {
  buildPublicSharePayload,
  createAndCopyPublicShareLink,
  SHARE_LINK_COPIED_MESSAGE,
} from "../utils/publicShareLinks";
import { formatLocalDate, toTimeValue } from "../utils/dateTime";
import { readApiJson } from "../utils/apiResponse";
import { fetchContentLibrary, mutateContentLibrary, contentMutationForAction } from "../utils/contentApi";
import { createContentRowFromApi, LIBRARY_TABS } from "../components/content/contentData";

const PURPLE = "#9C4FFF";
const BLUE = "#3451F4";
const TEXT = "#54595F";
const BORDER = "#EAECF0";
const SOFT_PURPLE_HOVER_BG = "#F5F3FF";
const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const DEFAULT_SEQUENCE_ENTITY_TYPE = "job";
const ADMIN_ROLES = new Set(["admin", "administrator", "system_user"]);
const VALID_CONTENT_TAB_IDS = new Set(CONTENT_TABS.map((tab) => tab.id));
const CONTENT_TYPE_KEY_BY_TAB = {
  job_postings: "job_posting",
  sequences: "sequence",
  templates: "template",
  pitch_packages: "pitch_package",
  proposals: "proposal",
  fee_agreements: "fee_agreement",
};
const CONTENT_TYPE_FILTER_OPTIONS = [
  { label: "Job Posting", value: "job_postings" },
  { label: "Sequence", value: "sequences" },
  { label: "Template", value: "templates" },
  { label: "Pitch Package", value: "pitch_packages" },
  { label: "Proposal", value: "proposals" },
  { label: "Fee Agreement", value: "fee_agreements" },
];
const CONTENT_FILTER_LABEL_BY_TAB_ID = CONTENT_TYPE_FILTER_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});
const CONTENT_FILTER_TYPE_TO_TAB_ID = CONTENT_TYPE_FILTER_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.value;
  acc[String(option.label || "").trim().toLowerCase()] = option.value;
  return acc;
}, {});
const SEQUENCE_STATUS_MENU_OPTIONS = [
  { label: "Draft", value: "Draft" },
  { label: "Active", value: "Active" },
  { label: "Paused", value: "Paused" },
  { label: "Completed", value: "Completed" },
  { label: "Archived", value: "Archived" },
];
const CONTENT_TYPE_STYLES = {
  job_posting: { backgroundColor: "#EEF4FF", color: "#3451F4" },
  sequence: { backgroundColor: "#F5F3FF", color: "#7C3AED" },
  template: { backgroundColor: "#ECFDF3", color: "#16A34A" },
  pitch_package: { backgroundColor: "#FFF4E5", color: "#B54708" },
  proposal: { backgroundColor: "#EFF8FF", color: "#175CD3" },
  fee_agreement: { backgroundColor: "#FDF2FA", color: "#C11574" },
};
const CONTENT_STATUS_OPTIONS_BY_TYPE = {
  job_posting: [
    "Draft",
    "Open",
    "For Review",
    "Approved",
    "Scheduled",
    "Closed",
    "Archived",
  ].map((value) => ({ label: value, value })),
  sequence: SEQUENCE_STATUS_MENU_OPTIONS,
  template: ["Draft", "Active", "Archived"].map((value) => ({ label: value, value })),
  pitch_package: ["Draft", "Active", "Archived"].map((value) => ({ label: value, value })),
  proposal: ["Draft", "Pending", "Approved", "Rejected", "Completed", "Archived"].map(
    (value) => ({ label: value, value })
  ),
  fee_agreement: ["Draft", "Pending", "Active", "Completed", "Archived"].map((value) => ({
    label: value,
    value,
  })),
};
const USE_CONTENT_OPTIONS_BY_TYPE = {
  sequence: [
    { type: "label", label: "Sequence", key: "use-content-sequence-label" },
    { label: "Add Candidates", value: "add_candidates", insetLevel: 1 },
    { label: "Add Contacts", value: "add_contacts", insetLevel: 1 },
    { label: "Add Leads", value: "add_leads", insetLevel: 1 },
    { label: "Add Companies", value: "add_companies", insetLevel: 1 },
  ],
  template: [
    { type: "label", label: "Template", key: "use-content-template-label" },
    { label: "Send To Candidates", value: "send_to_candidates", insetLevel: 1 },
    { label: "Send To Contacts", value: "send_to_contacts", insetLevel: 1 },
    { label: "Send To Leads", value: "send_to_leads", insetLevel: 1 },
    { label: "Send To Companies", value: "send_to_companies", insetLevel: 1 },
  ],
  pitch_package: [
    { type: "label", label: "Pitch Package", key: "use-content-pitch-package-label" },
    { label: "Submit Candidate", value: "submit_candidate", insetLevel: 1 },
    { label: "Share Package", value: "share_package", insetLevel: 1 },
  ],
  proposal: [
    { type: "label", label: "Proposal", key: "use-content-proposal-label" },
    { label: "Send Proposal", value: "send_proposal", insetLevel: 1 },
    { label: "Convert To Client", value: "convert_to_client", insetLevel: 1 },
  ],
  fee_agreement: [
    { type: "label", label: "Fee Agreement", key: "use-content-fee-agreement-label" },
    { label: "Send Agreement", value: "send_agreement", insetLevel: 1 },
    { label: "Track Signature", value: "track_signature", insetLevel: 1 },
  ],
};
const USE_CONTENT_OPTION_TYPE_ORDER = [
  "sequence",
  "template",
  "pitch_package",
  "proposal",
  "fee_agreement",
];
const PUBLISH_OPTIONS_BY_TYPE = {
  job_posting: [{ label: "Publish", value: "publish_job_posting" }],
  sequence: [{ label: "Publish", value: "publish_sequence" }],
  template: [{ label: "Publish", value: "publish_template" }],
};
const EXPORT_FORMAT_OPTIONS = [
  { label: "PDF", value: "pdf" },
  { label: "DOCX", value: "docx" },
  { label: "CSV", value: "csv" },
];
const PUBLISH_STATUS_BY_TYPE = {
  job_posting: "Open",
  sequence: "Active",
  template: "Active",
};
const IMPORTED_CONTENT_SOURCE_HINTS = new Set([
  "job board",
  "linkedin",
  "imported",
  "csv import",
  "api import",
  "external import",
  "referral",
]);

function createDefaultContentFilters() {
  return {
    aiRequest: "",
    contentType: "",
    statuses: [],
    owners: [],
    sources: [],
    dateTerms: [],
    usageStates: [],
    activityTerms: [],
    contentTerms: [],
  };
}

function getStatusChipSx(status) {
  const style = STATUS_STYLES[status] || {
    backgroundColor: "#F2F4F7",
    color: "#667085",
  };

  return {
    ...style,
    borderRadius: "999px",
    fontSize: 10,
    fontWeight: 600,
    height: 22,
    px: 1,
    lineHeight: 1.1,
  };
}

function getContentTypeChipSx(type) {
  const style = CONTENT_TYPE_STYLES[type] || {
    backgroundColor: "#F4F4F5",
    color: "#52525B",
  };

  return {
    ...style,
    borderRadius: "999px",
    fontSize: 10,
    fontWeight: 700,
    height: 22,
    px: 1,
    lineHeight: 1.1,
  };
}

function getRelatedEntityChipSx() {
  return {
    height: 22,
    borderRadius: "999px",
    backgroundColor: "#F8FAFC",
    border: "1px solid #D0D5DD",
    color: "#344054",
    fontSize: 10,
    fontWeight: 600,
    "& .MuiChip-label": {
      px: 1,
    },
  };
}

function titleCaseWords(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function parseCandidateActivity(value) {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }

  return typeof value === "object" ? value : null;
}

function normalizeCandidateActivityMetrics(sequence = {}) {
  const rawActivity = parseCandidateActivity(sequence.candidate_activity);

  if (rawActivity) {
    const normalizedEntries = Object.entries(rawActivity)
      .map(([key, value]) => {
        const normalizedKey = String(key || "")
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "");
        const normalizedValue = readOptionalMetric(value, { min: 0, round: true });
        return normalizedKey && normalizedValue != null ? [normalizedKey, normalizedValue] : null;
      })
      .filter(Boolean);

    if (normalizedEntries.length) {
      return Object.fromEntries(normalizedEntries);
    }
  }

  const fallbackActivity = {
    active: readOptionalMetric(sequence.candidates_active, { min: 0, round: true }),
    paused: readOptionalMetric(sequence.candidates_paused, { min: 0, round: true }),
    failed: readOptionalMetric(sequence.candidates_failed, { min: 0, round: true }),
    bounced: readOptionalMetric(sequence.candidates_bounced, { min: 0, round: true }),
  };

  return Object.values(fallbackActivity).some((value) => value != null) ? fallbackActivity : null;
}

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function readOptionalMetric(value, { min = 0, max = null, round = false } = {}) {
  if (value == null || value === "") return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;

  const normalizedValue = round ? Math.round(numericValue) : numericValue;
  const lowerBoundedValue = Math.max(min, normalizedValue);

  if (Number.isFinite(max)) {
    return clampNumber(lowerBoundedValue, min, max);
  }

  return lowerBoundedValue;
}

function formatDateLabel(value) {
  if (!value) return "-";
  return formatLocalDate(value, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }, String(value));
}

const getToken = () => {
  const direct =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("idToken") ||
    localStorage.getItem("id_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("idToken") ||
    sessionStorage.getItem("id_token");

  if (direct && String(direct).trim()) return String(direct).trim();

  const tryJsonKeys = ["user", "auth", "session", "profile"];
  for (const key of tryJsonKeys) {
    try {
      const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const token =
        parsed?.token ||
        parsed?.accessToken ||
        parsed?.access_token ||
        parsed?.jwt ||
        parsed?.idToken ||
        parsed?.id_token;
      if (token && String(token).trim()) return String(token).trim();
    } catch {
      // Ignore malformed cached auth data.
    }
  }

  return "";
};

const getAuthHeaders = () => {
  const token = getToken();
  if (!token) return {};

  const rawToken = token.replace(/^Bearer\s+/i, "");
  return {
    Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
    "x-access-token": rawToken,
  };
};

function toPositiveNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
}

function extractPositiveId(value) {
  const direct = toPositiveNumber(value);
  if (direct) return direct;

  const matches = String(value || "").match(/\d+/g);
  if (!matches?.length) return null;
  return toPositiveNumber(matches[matches.length - 1]);
}

function resolveContentTypeKey(tabId, rows = []) {
  const fromRows = Array.isArray(rows) ? rows.find((row) => row?.content_type_key) : null;
  return fromRows?.content_type_key || CONTENT_TYPE_KEY_BY_TAB[tabId] || "content";
}

export function resolveRowContentTypeKey(row = {}, fallbackTabId = "") {
  const explicitContentTypeKey = String(
    row?.content_type_key || row?.contentType || row?.content_type || ""
  ).trim();

  return explicitContentTypeKey || CONTENT_TYPE_KEY_BY_TAB[fallbackTabId] || "content";
}

export function buildUseContentOptions(typeKeys = []) {
  const normalizedTypeKeySet = new Set(
    (Array.isArray(typeKeys) ? typeKeys : [])
      .map((typeKey) => String(typeKey || "").trim())
      .filter((typeKey) => USE_CONTENT_OPTIONS_BY_TYPE[typeKey])
  );

  return USE_CONTENT_OPTION_TYPE_ORDER.filter((typeKey) =>
    normalizedTypeKeySet.has(typeKey)
  ).flatMap((typeKey, groupIndex) => {
    const typeOptions = USE_CONTENT_OPTIONS_BY_TYPE[typeKey] || [];
    const normalizedOptions = typeOptions.map((option, optionIndex) => ({
      ...(option && typeof option === "object"
        ? option
        : {
            label: String(option ?? ""),
            value: String(option ?? ""),
          }),
      key:
        option?.key ||
        `use-content-${typeKey}-${option?.value || option?.label || optionIndex}`,
      contentTypeKey: typeKey,
    }));

    return groupIndex === 0
      ? normalizedOptions
      : [{ type: "divider", key: `use-content-divider-${typeKey}` }, ...normalizedOptions];
  });
}

function normalizeSearchValue(value) {
  return String(value || "").replace(/\s+/g, " ").trim().toLowerCase();
}

function collectSearchValues(values = []) {
  return (Array.isArray(values) ? values : [values]).flatMap((value) => {
    if (Array.isArray(value)) {
      return collectSearchValues(value);
    }

    if (value == null) {
      return [];
    }

    if (typeof value === "number" || typeof value === "boolean") {
      return [String(value)];
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed ? [trimmed] : [];
    }

    if (typeof value === "object") {
      return collectSearchValues(Object.values(value));
    }

    return [];
  });
}

function buildCurrentUserNameTokens(user = {}) {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");

  return Array.from(
    new Set(
      collectSearchValues([
        user?.name,
        user?.full_name,
        user?.fullName,
        fullName,
        user?.email ? String(user.email).split("@")[0] : "",
      ])
        .map(normalizeSearchValue)
        .filter(Boolean)
    )
  );
}

function resolveContentOwnerScopeLabel(row = {}, user = {}) {
  const ownerName = normalizeSearchValue(row?.owner_name || row?.owner || "");
  if (!ownerName) {
    return "All";
  }

  const userNameTokens = buildCurrentUserNameTokens(user);
  return userNameTokens.includes(ownerName) ? "Mine" : "Team Member";
}

function resolveContentSourceLabel(row = {}) {
  const explicitSources = collectSearchValues([
    row?.content_source_label,
    row?.content_source,
    row?.content_origin,
    row?.creation_source,
    row?.source_category,
    row?.origin,
    row?.generated_by,
    row?.generated_via,
    row?.source,
  ]).map(normalizeSearchValue);

  if (
    row?.is_ai_generated ||
    row?.ai_generated ||
    row?.generated_by_ai ||
    explicitSources.some((value) => value.includes("ai"))
  ) {
    return "AI Generated";
  }

  if (
    row?.is_imported ||
    row?.imported ||
    row?.import_source ||
    row?.imported_from ||
    explicitSources.some(
      (value) =>
        value.includes("import") || IMPORTED_CONTENT_SOURCE_HINTS.has(value)
    )
  ) {
    return "Imported";
  }

  return "Human Created";
}

function resolveUsageStateLabel(row = {}) {
  const usageValue = Number(
    row?.usage_value ?? row?.applications ?? row?.application_count ?? row?.enrollment_count ?? 0
  );
  return Number.isFinite(usageValue) && usageValue > 0 ? "In Use" : "Not In Use";
}

function resolveRowDateValue(row = {}, kind = "created") {
  if (kind === "created") {
    return row?.created_label || row?.created_at || row?.updated_label || row?.last_used_label || "";
  }

  if (kind === "updated") {
    return row?.updated_label || row?.updated_at || row?.created_label || row?.last_used_label || "";
  }

  return row?.last_used_label || row?.last_used_at || row?.updated_label || "";
}

function buildContentSearchHaystack(row = {}, user = {}) {
  const sourceLabel = resolveContentSourceLabel(row);
  const ownerScopeLabel = resolveContentOwnerScopeLabel(row, user);
  const usageStateLabel = resolveUsageStateLabel(row);
  const tags = getRowTags(row);
  const sharingValues =
    row?.sharing && typeof row.sharing === "object"
      ? collectSearchValues([
          row.sharing.type,
          row.sharing.label,
          row.sharing.values,
          row.sharing.extra,
        ])
      : [];

  return collectSearchValues([
    "Content",
    row?.content_name,
    row?.content_description,
    row?.description,
    row?.summary,
    row?.goal,
    row?.name,
    row?.title,
    row?.job_title,
    "Description",
    row?.content_description,
    row?.description,
    row?.summary,
    row?.goal,
    "Tags",
    tags,
    "Content Type",
    row?.content_type_label,
    row?.content_type_key,
    row?.contentType,
    "Status",
    row?.status,
    row?.statusRaw,
    "Owner",
    "All",
    ownerScopeLabel,
    row?.owner_name,
    row?.owner,
    row?.hiring_manager,
    "Source",
    sourceLabel,
    row?.source,
    "Date Filters",
    "Created Date",
    resolveRowDateValue(row, "created"),
    row?.created_at,
    "Updated Date",
    resolveRowDateValue(row, "updated"),
    row?.updated_at,
    "Last Used",
    resolveRowDateValue(row, "last_used"),
    row?.last_used_at,
    "Usage",
    usageStateLabel,
    row?.usage_label,
    row?.usage_context,
    row?.usage_value,
    row?.applications,
    "Content",
    row?.related_entity_label,
    row?.entity_type,
    row?.entity_type_raw,
    "Company",
    row?.company_name,
    row?.company_domain,
    row?.company,
    row?.website,
    "Job",
    row?.job_title,
    row?.job_name,
    row?.job,
    "Candidate",
    row?.candidate_name,
    row?.candidate,
    "Lead",
    row?.lead_name,
    row?.lead,
    "Contact",
    row?.contact_name,
    row?.contact,
    "Client",
    row?.client_name,
    row?.client,
    "Sequence Content",
    row?.sequence_content,
    row?.goal,
    row?.from_name,
    row?.from_email,
    row?.step_count ? `${row.step_count} steps` : "",
    "Template Content",
    row?.template_content,
    row?.content,
    "Proposal Content",
    row?.proposal_content,
    row?.proposal_body,
    "Agreement Content",
    row?.agreement_content,
    row?.fee_agreement_content,
    row?.agreement_body,
    "Pitch Package",
    row?.pitch_package_content,
    row?.pitch_content,
    "Activity",
    row?.performance_label,
    row?.performance_display,
    row?.latest_note_title,
    row?.latest_note_body,
    row?.note_title,
    row?.note_body,
    row?.notes,
    row?.notes_updated_at,
    row?.tech_stack,
    sharingValues,
  ])
    .join(" ")
    .toLowerCase();
}

function uniqStrings(values = []) {
  return Array.from(
    new Set(
      collectSearchValues(values)
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );
}

function normalizeContentFilters(filters = {}) {
  const contentTypeValues = uniqStrings(filters?.contentType);

  return {
    ...createDefaultContentFilters(),
    aiRequest: String(filters?.aiRequest || "").trim(),
    contentType: contentTypeValues.length ? contentTypeValues[contentTypeValues.length - 1] : "",
    statuses: uniqStrings(filters?.statuses),
    owners: uniqStrings(filters?.owners),
    sources: uniqStrings(filters?.sources),
    dateTerms: uniqStrings(filters?.dateTerms),
    usageStates: uniqStrings(filters?.usageStates),
    activityTerms: uniqStrings(filters?.activityTerms),
    contentTerms: uniqStrings(filters?.contentTerms),
  };
}

function buildContentDateHaystack(row = {}) {
  return collectSearchValues([
    "Date Filters",
    "Created Date",
    resolveRowDateValue(row, "created"),
    row?.created_at,
    "Updated Date",
    resolveRowDateValue(row, "updated"),
    row?.updated_at,
    "Last Used",
    resolveRowDateValue(row, "last_used"),
    row?.last_used_at,
  ])
    .join(" ")
    .toLowerCase();
}

function buildContentActivityHaystack(row = {}) {
  return collectSearchValues([
    "Activity",
    row?.performance_label,
    row?.performance_display,
    row?.latest_note_title,
    row?.latest_note_body,
    row?.note_title,
    row?.note_body,
    row?.notes,
    row?.notes_updated_at,
    "Usage",
    resolveUsageStateLabel(row),
    row?.usage_label,
    row?.usage_context,
    row?.usage_value,
  ])
    .join(" ")
    .toLowerCase();
}

function matchesAllTerms(haystack = "", value = "") {
  const normalizedHaystack = String(haystack || "").toLowerCase();
  const terms = String(value || "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  if (!terms.length) return true;
  return terms.every((term) => normalizedHaystack.includes(term));
}

function matchesAnySelectedValue(haystack = "", values = []) {
  const normalizedHaystack = normalizeSearchValue(haystack);
  const selectedValues = uniqStrings(values)
    .map(normalizeSearchValue)
    .filter(Boolean);

  if (!selectedValues.length) return true;
  return selectedValues.some((value) => normalizedHaystack.includes(value));
}

export function matchesRowWithContentFilters(row = {}, user = {}, filters = {}) {
  const normalizedFilters = normalizeContentFilters(filters);
  const searchHaystack = buildContentSearchHaystack(row, user);
  const contentTypeHaystack = collectSearchValues([
    row?.content_type_label,
    row?.content_type_key,
    row?.contentType,
    row?.tabId,
    CONTENT_FILTER_LABEL_BY_TAB_ID[row?.tabId],
  ])
    .join(" ")
    .toLowerCase();
  const statusHaystack = collectSearchValues([row?.status, row?.statusRaw])
    .join(" ")
    .toLowerCase();
  const ownerHaystack = collectSearchValues([
    "Owner",
    "All",
    resolveContentOwnerScopeLabel(row, user),
    row?.owner_name,
    row?.owner,
  ])
    .join(" ")
    .toLowerCase();
  const sourceHaystack = collectSearchValues([
    resolveContentSourceLabel(row),
    row?.source,
    row?.content_source_label,
    row?.content_source,
    row?.content_origin,
    row?.origin,
  ])
    .join(" ")
    .toLowerCase();
  const usageHaystack = collectSearchValues([
    resolveUsageStateLabel(row),
    row?.usage_label,
    row?.usage_context,
    row?.usage_value,
  ])
    .join(" ")
    .toLowerCase();

  if (!matchesAllTerms(searchHaystack, normalizedFilters.aiRequest)) return false;
  if (
    normalizedFilters.contentType &&
    !matchesAnySelectedValue(contentTypeHaystack, [normalizedFilters.contentType])
  ) {
    return false;
  }
  if (!matchesAnySelectedValue(statusHaystack, normalizedFilters.statuses)) return false;
  if (!matchesAnySelectedValue(ownerHaystack, normalizedFilters.owners)) return false;
  if (!matchesAnySelectedValue(sourceHaystack, normalizedFilters.sources)) return false;
  if (!matchesAnySelectedValue(buildContentDateHaystack(row), normalizedFilters.dateTerms)) {
    return false;
  }
  if (!matchesAnySelectedValue(usageHaystack, normalizedFilters.usageStates)) return false;
  if (!matchesAnySelectedValue(buildContentActivityHaystack(row), normalizedFilters.activityTerms)) {
    return false;
  }
  if (!matchesAnySelectedValue(searchHaystack, normalizedFilters.contentTerms)) return false;

  return true;
}

function resolveActivityEntityType(row = {}) {
  const rawType = String(
    row?.entity_type_raw ||
      row?.entity_type ||
      row?.related_entity_label ||
      row?.content_type_label ||
      ""
  )
    .trim()
    .toLowerCase();

  if (rawType.includes("candidate")) return "candidate";
  if (rawType.includes("company")) return "company";
  if (rawType.includes("job")) return "job";
  return "lead";
}

function resolveActivityEntityId(row = {}, index = 0) {
  const candidates = [
    row?.entity_id,
    row?.entityId,
    row?.job_id,
    row?.jobId,
    row?.lead_id,
    row?.leadId,
    row?.candidate_id,
    row?.candidateId,
    row?.company_id,
    row?.companyId,
    row?.client_id,
    row?.clientId,
    row?.sequenceId,
    row?.id,
  ];

  for (const value of candidates) {
    const nextId = extractPositiveId(value);
    if (nextId) return nextId;
  }

  return index + 1;
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function getAvatarLetters(value, fallback = "SQ") {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return fallback;
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

function deriveSequenceAnalytics(sequence = {}, index = 0) {
  const stepCount = Math.max(0, Number(sequence.step_count || 0));
  const enrollmentCount = Math.max(0, Number(sequence.enrollment_count || 0));
  const score = readOptionalMetric(sequence.score, { min: 0, max: 99, round: true });
  const contacted = readOptionalMetric(sequence.contacted, { min: 0, round: true });
  const openedRate = readOptionalMetric(sequence.opened_rate, { min: 0, max: 99, round: true });
  const clickedRate = readOptionalMetric(sequence.clicked_rate, { min: 0, max: 99, round: true });
  const repliedRate = readOptionalMetric(sequence.replied_rate, { min: 0, max: 99, round: true });
  const interestedRate = readOptionalMetric(sequence.interested_rate, { min: 0, max: 99, round: true });
  const candidateActivityValues = normalizeCandidateActivityMetrics(sequence);
  const hasCandidateActivity = Object.values(candidateActivityValues || {}).some((value) => value != null);
  const candidateActivity = hasCandidateActivity ? candidateActivityValues : null;
  const candidateActivityTotal = hasCandidateActivity
    ? Object.values(candidateActivityValues || {}).reduce((total, value) => total + (value || 0), 0)
    : null;

  return {
    sequenceScore: score,
    sequenceScoreSort: score == null ? 999 : -score,
    candidateActivity,
    candidateActivitySort: candidateActivityTotal == null ? 999 : -candidateActivityTotal,
    steps_label: `${stepCount} step${stepCount === 1 ? "" : "s"} by interval`,
    contacted,
    openedRate,
    openedCount:
      contacted != null && openedRate != null
        ? Math.max(0, Math.round((contacted * openedRate) / 100))
        : null,
    openedRateSort: openedRate == null ? 999 : -openedRate,
    clickedRate,
    clickedCount:
      contacted != null && clickedRate != null
        ? Math.max(0, Math.round((contacted * clickedRate) / 100))
        : null,
    clickedRateSort: clickedRate == null ? 999 : -clickedRate,
    repliedRate,
    repliedCount:
      contacted != null && repliedRate != null
        ? Math.max(0, Math.round((contacted * repliedRate) / 100))
        : null,
    repliedRateSort: repliedRate == null ? 999 : -repliedRate,
    interestedRate,
    interestedCount:
      contacted != null && interestedRate != null
        ? Math.max(0, Math.round((contacted * interestedRate) / 100))
        : null,
    interestedRateSort: interestedRate == null ? 999 : -interestedRate,
    last_used_label: formatDateLabel(sequence.updated_at || sequence.created_at),
    lastUsedSort: (() => {
      const lastUsedTime = toTimeValue(sequence.updated_at || sequence.created_at);
      return lastUsedTime ? -lastUsedTime : -(Date.now() - index);
    })(),
    owner_initials: getAvatarLetters(sequence.owner_name, "RS"),
    activity_total: candidateActivityTotal == null ? enrollmentCount : candidateActivityTotal + enrollmentCount,
  };
}

function isSequenceRow(row) {
  return row?.contentType === "sequence";
}

function isTemplateRow(row) {
  return row?.contentType === "template";
}

function getRowPrimaryLabel(row) {
  return row?.content_name || row?.job_title || row?.name || "Untitled";
}

function getRowNoteTitle(row = {}) {
  return String(row?.latest_note_title || row?.note_title || "Note").trim() || "Note";
}

function getRowNoteBody(row = {}) {
  return sanitizeImportedSourceText(
    row?.latest_note_body || row?.note_body || row?.notes || ""
  );
}

function getRowTags(row = {}) {
  const values = [
    row?.tags,
    row?.tag_list,
    row?.tagList,
    row?.content_tags,
    row?.contentTags,
  ];

  return [...new Set(
    values
      .flatMap((value) =>
        Array.isArray(value) ? value : String(value || "").split(",")
      )
      .map((value) => String(value || "").trim())
      .filter(Boolean)
  )];
}

function NotesAndTagsSection({ row }) {
  const noteBody = getRowNoteBody(row);
  const noteTitle = getRowNoteTitle(row);
  const tags = getRowTags(row);

  if (!noteBody && !tags.length) return null;

  return (
    <>
      <Divider sx={{ my: 0.5 }} />

      {tags.length ? (
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
            Tags
          </Typography>
          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{
                  height: 24,
                  borderRadius: "999px",
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #D0D5DD",
                  color: "#344054",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            ))}
          </Stack>
        </Box>
      ) : null}

      {noteBody ? (
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
            {noteTitle}
          </Typography>
          <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: TEXT }}>
            {noteBody}
          </Typography>
        </Box>
      ) : null}
    </>
  );
}

export function normalizeSequenceRow(sequence = {}, index = 0) {
  const updatedTime = toTimeValue(sequence.updated_at || sequence.created_at);
  const analytics = deriveSequenceAnalytics(sequence, index);
  const sequenceName = String(sequence.name || "").trim() || `Sequence ${index + 1}`;
  const entityTypeLabel = getSequenceEntityLabel(
    sequence.entity_type || DEFAULT_SEQUENCE_ENTITY_TYPE
  );
  const enrollmentCount = Number(sequence.enrollment_count || 0);
  const replyRate = analytics.repliedRate;

  return {
    id: sequence.id || `sequence-${index + 1}`,
    tabId: "sequences",
    contentType: "sequence",
    note_title: sequence.note_title,
    note_body: sequence.note_body,
    notes: sequence.notes,
    content_type_key: "sequence",
    content_type_label: "Sequence",
    sortOrder: index + 1,
    updatedAtSort: updatedTime ? -updatedTime : -(Date.now() - index),
    content_name: sequenceName,
    name: sequenceName,
    statusRaw: String(sequence.status || "draft").trim().toLowerCase() || "draft",
    status: titleCaseWords(sequence.status || "draft"),
    owner_name: String(sequence.owner_name || "").trim() || "Unassigned",
    owner_avatar_url: String(sequence.owner_avatar_url || "").trim(),
    entity_type: entityTypeLabel,
    entity_type_raw: String(sequence.entity_type || DEFAULT_SEQUENCE_ENTITY_TYPE).trim().toLowerCase(),
    step_count: Number(sequence.step_count || 0),
    enrollment_count: enrollmentCount,
    from_name: String(sequence.from_name || "").trim(),
    from_email: String(sequence.from_email || "").trim(),
    goal: String(sequence.goal || "").trim(),
    description: String(sequence.description || "").trim(),
    summary:
      String(sequence.goal || "").trim() ||
      String(sequence.description || "").trim() ||
      "No goal yet",
    content_description:
      String(sequence.goal || "").trim() ||
      String(sequence.description || "").trim() ||
      "No goal yet",
    related_entity_label: entityTypeLabel,
    usage_value: enrollmentCount,
    usage_context: `${getSequenceEntityLabel(
      sequence.entity_type || DEFAULT_SEQUENCE_ENTITY_TYPE,
      true
    )} Added`,
    usage_label: `${enrollmentCount} ${getSequenceEntityLabel(
      sequence.entity_type || DEFAULT_SEQUENCE_ENTITY_TYPE,
      true
    )} Added`,
    usage_sort: -enrollmentCount,
    performance_value: replyRate,
    performance_label: "Reply Rate",
    performance_display: replyRate == null ? "—" : `${replyRate}%`,
    performance_sort: replyRate == null ? 0 : -replyRate,
    last_used_sort: analytics.lastUsedSort,
    created_at: sequence.created_at || null,
    updated_at: sequence.updated_at || null,
    created_label: formatDateLabel(sequence.created_at),
    updated_label: formatDateLabel(sequence.updated_at || sequence.created_at),
    ...analytics,
  };
}

function TabsBar({ activeTab, onChange, counts }) {
  return (
    <TabNavigationBar
      wrapperClassName="side-padding-16px"
      dividerColor={BORDER}
      items={CONTENT_TABS.map((tab) => ({
        value: tab.id,
        label: tab.label,
        count: counts?.[tab.id] ?? tab.badge,
      }))}
      onChange={(nextValue) => onChange(nextValue)}
      value={activeTab}
    />
  );
}

function DetailsDrawer({ open, row, onClose }) {
  if (!row) return null;

  if (isTemplateRow(row)) {
    return (
      <ReusableRightDrawer
        open={open}
        onClose={onClose}
        id="content-detail-drawer"
        width={380}
        headerLeft={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "999px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#EEF2FF",
              }}
            >
              <Eye size="16" color={PURPLE} variant="Linear" />
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: BLUE }}>
              Quick Template Detail
            </Typography>
          </Box>
        }
      >
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111827", mb: 1.5 }}>
          {row.content_name || row.job_title}
        </Typography>

        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: "1px solid #EEF2F7",
            backgroundColor: "#FAFBFF",
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.75 }}>
            Content Preview
          </Typography>
          <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: TEXT }}>
            {row.template_content}
          </Typography>
        </Box>

        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              label={row.content_type_label || "Template"}
              size="small"
              sx={getContentTypeChipSx(row.content_type_key || row.contentType)}
            />
            <Chip label={row.status} size="small" sx={getStatusChipSx(row.status)} />
            <Chip
              label={row.related_entity_label || "Candidate"}
              size="small"
              variant="outlined"
              sx={getRelatedEntityChipSx()}
            />
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {[
              row.delivered_rate == null ? "Delivered: —" : `${row.delivered_rate}% Delivered`,
              row.opened_rate == null ? "Opened: —" : `${row.opened_rate}% Opened`,
              row.clicked_rate == null ? "Clicked: —" : `${row.clicked_rate}% Clicked`,
              row.replied_rate == null ? "Replied: —" : `${row.replied_rate}% Replied`,
            ].map((label) => (
              <Chip
                key={label}
                label={label}
                size="small"
                sx={{
                  height: 24,
                  borderRadius: "999px",
                  backgroundColor: "#F5F3FF",
                  color: PURPLE,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            ))}
          </Stack>

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
              Owner
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT }}>{row.owner_name}</Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
              Usage
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT }}>
              {row.usage_label}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
              Performance
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT }}>
              {`${row.performance_label}: ${row.performance_display}`}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
              Last Used
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT }}>{row.last_used_label}</Typography>
          </Box>

          <NotesAndTagsSection row={row} />
        </Stack>
      </ReusableRightDrawer>
    );
  }

  if (isSequenceRow(row)) {
    return (
      <ReusableRightDrawer
        open={open}
        onClose={onClose}
        id="content-detail-drawer"
        width={380}
        headerLeft={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "999px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#EEF2FF",
              }}
            >
              <Eye size="16" color={PURPLE} variant="Linear" />
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: BLUE }}>
              Quick Sequence Detail
            </Typography>
          </Box>
        }
      >
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111827", mb: 1.5 }}>
          {row.name}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid #EEF2F7",
            backgroundColor: "#FAFBFF",
            mb: 2,
          }}
        >
          <Avatar
            src={row.owner_avatar_url || undefined}
            sx={{
              width: 42,
              height: 42,
              bgcolor: row.owner_avatar_url ? "transparent" : "#EEF2FF",
              color: PURPLE,
              fontWeight: 800,
            }}
          >
            {!row.owner_avatar_url ? getAvatarLetters(row.owner_name) : null}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700, color: "#111827" }}>{row.owner_name}</Typography>
            <Typography sx={{ fontSize: 12, color: "#667085" }}>
              {row.entity_type} sequence
            </Typography>
          </Box>
        </Box>

        <Stack spacing={1.25}>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
              Goal
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT }}>
              {row.goal || row.description || "No goal yet"}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
              Status
            </Typography>
            <Chip label={row.status} size="small" sx={getStatusChipSx(row.status)} />
          </Box>

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
              Sender
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT }}>
              {[row.from_name, row.from_email].filter(Boolean).join(" | ") || "Not set"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              label={`${row.step_count} steps`}
              size="small"
              sx={{
                height: 24,
                borderRadius: "999px",
                backgroundColor: "#F5F3FF",
                color: PURPLE,
                fontSize: 11,
                fontWeight: 600,
              }}
            />
            <Chip
              label={`${row.enrollment_count} enrolled`}
              size="small"
              sx={{
                height: 24,
                borderRadius: "999px",
                backgroundColor: "#EEF4FF",
                color: BLUE,
                fontSize: 11,
                fontWeight: 600,
              }}
            />
          </Stack>

          <Divider sx={{ my: 0.5 }} />

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
              Created
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT }}>{row.created_label}</Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
              Last Updated
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT }}>{row.updated_label}</Typography>
          </Box>

          {row.description ? (
            <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: TEXT }}>
              {row.description}
            </Typography>
          ) : null}

          <NotesAndTagsSection row={row} />
        </Stack>
      </ReusableRightDrawer>
    );
  }

  return (
    <ReusableRightDrawer
      open={open}
      onClose={onClose}
      id="content-detail-drawer"
      width={380}
      headerLeft={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: "999px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#EEF2FF",
            }}
          >
            <Eye size="16" color={PURPLE} variant="Linear" />
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: BLUE }}>
            Quick Content Detail
          </Typography>
        </Box>
      }
    >
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111827", mb: 1.5 }}>
        {row.content_name || row.job_title}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          p: 1.5,
          borderRadius: 2,
          border: "1px solid #EEF2F7",
          backgroundColor: "#FAFBFF",
          mb: 2,
        }}
      >
        <Avatar sx={{ width: 42, height: 42, bgcolor: row.company_color, fontWeight: 700 }}>
          {row.company_name?.[0] || "C"}
        </Avatar>
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#111827" }}>{row.company_name}</Typography>
          <Typography sx={{ fontSize: 12, color: "#667085" }}>{row.company_domain}</Typography>
        </Box>
      </Box>

      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip
            label={row.content_type_label || "Content"}
            size="small"
            sx={getContentTypeChipSx(row.content_type_key || row.contentType)}
          />
          <Chip label={row.status} size="small" sx={getStatusChipSx(row.status)} />
          <Chip
            label={row.related_entity_label || "Lead"}
            size="small"
            variant="outlined"
            sx={getRelatedEntityChipSx()}
          />
        </Stack>

        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
            Owner
          </Typography>
          <Typography sx={{ fontSize: 13, color: TEXT }}>{row.owner_name}</Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
            Usage
          </Typography>
          <Typography sx={{ fontSize: 13, color: TEXT }}>{row.usage_label}</Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
            Performance
          </Typography>
          <Typography sx={{ fontSize: 13, color: TEXT }}>
            {`${row.performance_label}: ${row.performance_display}`}
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.75 }}>
            Last Used
          </Typography>
          <Typography sx={{ fontSize: 13, color: TEXT }}>{row.last_used_label}</Typography>
        </Box>

        {Array.isArray(row.tech_stack) && row.tech_stack.length ? (
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
              Tech Stack
            </Typography>
            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
              {row.tech_stack.map((item) => (
                <Chip
                  key={item}
                  label={item}
                  size="small"
                  sx={{
                    height: 24,
                    borderRadius: "999px",
                    backgroundColor: "#F5F3FF",
                    color: PURPLE,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                />
              ))}
            </Stack>
          </Box>
        ) : null}

        {row.source ? (
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.5 }}>
              Source
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT }}>{row.source}</Typography>
          </Box>
        ) : null}

        <Divider sx={{ my: 0.5 }} />

        <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: TEXT }}>
          {row.description}
        </Typography>

        <NotesAndTagsSection row={row} />
      </Stack>
    </ReusableRightDrawer>
  );
}

function ContentFiltersDrawer({
  open,
  onClose,
  draft,
  setDraft,
  endpoint,
  onClear,
  onApply,
  onAdvancedSearch,
}) {
  const directory = usePageFilterOptions(open, endpoint, draft, setDraft);
  const options = directory.options;
  const sectionLabelSx = {
    fontSize: 14,
    fontWeight: 700,
    color: BLUE,
    mb: 0.9,
    fontFamily: "Be Vietnam Pro",
  };
  const dividerSx = { my: 1.6, borderColor: "#EEF2F7" };
  const inputSx = popupFilterInputSx(PURPLE);
  const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const headerLeft = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "999px",
          border: `1px solid ${BLUE}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fff",
          flex: "0 0 auto",
        }}
      >
        <FilterSearch size={16} color={BLUE} variant="Outline" />
      </Box>
      <Typography data-popup-section
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: BLUE,
          fontFamily: "Be Vietnam Pro",
        }}
      >
        Filter by
      </Typography>
    </Box>
  );

  return (
    <ReusableRightDrawer
      open={open}
      onClose={onClose}
      id="content-filters-drawer"
      width={546}
      headerLeft={headerLeft}
      closeVariant="contained"
      closeColor={PURPLE}
      paperSx={{ overflow: "hidden" }}
      footer={null}
    >
      <Box sx={{ pb: 10 }}>
        {directory.loading && <Typography role="status">Loading filter values?</Typography>}
        {directory.error && <Alert severity="error">{directory.error}</Alert>}
        <Typography sx={sectionLabelSx}>AI Agent:</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Type your request"
          value={draft.aiRequest || ""}
          onChange={(event) => setField("aiRequest", event.target.value)}
          sx={inputSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 18, color: "#A78BFA" }} />
              </InputAdornment>
            ),
          }}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Content Type:</Typography>
        <PopupMultiSelectField
          options={options.contentTypes}
          {...directory.fieldProps("contentTypes")}
          value={draft.contentType ? [draft.contentType] : []}
          onChange={(next) => {
            const normalized = uniqStrings(next);
            setField("contentType", normalized.length ? normalized[normalized.length - 1] : "");
          }}
          placeholder="Job Posting, Sequence, Template..."
          inputSx={inputSx}
          freeSolo={false}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Status:</Typography>
        <PopupMultiSelectField
          options={options.statuses}
          {...directory.fieldProps("statuses")}
          value={draft.statuses || []}
          onChange={(next) => setField("statuses", uniqStrings(next))}
          placeholder="Draft, Active, Published, Archived"
          inputSx={inputSx}
          freeSolo={false}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Owner:</Typography>
        <PopupMultiSelectField
          options={options.owners}
          {...directory.fieldProps("owners")}
          value={draft.owners || []}
          onChange={(next) => setField("owners", uniqStrings(next))}
          placeholder="All, Mine, Team Member, or owner name"
          inputSx={inputSx}
          freeSolo={false}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Source:</Typography>
        <PopupMultiSelectField
          options={options.sources}
          {...directory.fieldProps("sources")}
          value={draft.sources || []}
          onChange={(next) => setField("sources", uniqStrings(next))}
          placeholder="Human Created, AI Generated, Imported"
          inputSx={inputSx}
          freeSolo={false}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Date Filters:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={options.dateTerms}
          {...directory.fieldProps("dateTerms")}
          value={draft.dateTerms || []}
          onChange={(next) => setField("dateTerms", uniqStrings(next))}
          placeholder="Created Date, Updated Date, Last Used"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Usage:</Typography>
        <PopupMultiSelectField
          options={options.usageStates}
          {...directory.fieldProps("usageStates")}
          value={draft.usageStates || []}
          onChange={(next) => setField("usageStates", uniqStrings(next))}
          placeholder="In Use or Not In Use"
          inputSx={inputSx}
          freeSolo={false}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Activity:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={options.activities}
          {...directory.fieldProps("activities")}
          value={draft.activityTerms || []}
          onChange={(next) => setField("activityTerms", uniqStrings(next))}
          placeholder="Search activity, notes, or performance"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Content:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={options.contentTerms}
          {...directory.fieldProps("contentTerms")}
          value={draft.contentTerms || []}
          onChange={(next) => setField("contentTerms", uniqStrings(next))}
          placeholder="Search content name, tags, company, job, candidate, lead, contact, client..."
          inputSx={inputSx}
        />
      </Box>

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 5,
          backgroundColor: "#fff",
          borderTop: "1px solid #EEF2F7",
          px: 2,
          py: 2,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Button
            variant="contained"
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 12,
              fontFamily: "Be Vietnam Pro",
              backgroundColor: PURPLE,
              boxShadow: "none",
              px: 2.2,
              "&:hover": { backgroundColor: "#6D28D9", boxShadow: "none" },
            }}
            onClick={onAdvancedSearch}
          >
            Advance Search
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="outlined"
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 12,
              fontFamily: "Be Vietnam Pro",
              borderColor: PURPLE,
              color: PURPLE,
              px: 2.2,
              "&:hover": {
                borderColor: "#6D28D9",
                color: "#6D28D9",
                backgroundColor: "transparent",
              },
            }}
            onClick={onClear}
          >
            Clear Filters
          </Button>

          <Button
            variant="contained"
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 12,
              fontFamily: "Be Vietnam Pro",
              backgroundColor: PURPLE,
              boxShadow: "none",
              px: 2.2,
              "&:hover": { backgroundColor: "#6D28D9", boxShadow: "none" },
            }}
            onClick={onApply}
          >
            Apply Filters
          </Button>
        </Stack>
      </Box>
    </ReusableRightDrawer>
  );
}

export default function Content() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuthController();
  const [activeTab, setActiveTab] = useState("job_postings");
  const [rowsByTab, setRowsByTab] = useState(() => createInitialRowsByTab());
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [filterPreset, setFilterPreset] = useState("all");
  const [contentStatFilterId, setContentStatFilterId] = useState(null);
  const [contentUsageMetricId, setContentUsageMetricId] = useState(
    DEFAULT_CONTENT_USAGE_STAT_OPTION_ID
  );
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [contentFilters, setContentFilters] = useState(() => createDefaultContentFilters());
  const [contentFilterDraft, setContentFilterDraft] = useState(() => createDefaultContentFilters());
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
  const [rowMenuItem, setRowMenuItem] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });
  const [isAddJobOpen, setIsAddJobOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useCsvImport();
  const [isAddToSequenceOpen, setIsAddToSequenceOpen] = useState(false);
  const [sequenceDialogEntityType, setSequenceDialogEntityType] = useState("job");
  const [editSequence, setEditSequence] = useState(null);
  const [isLoadingSequences, setIsLoadingSequences] = useState(false);
  const [sequencesLoadError, setSequencesLoadError] = useState("");
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [jobsLoadError, setJobsLoadError] = useState("");
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(true);
  const [libraryLoadError, setLibraryLoadError] = useState("");
  const [editContent, setEditContent] = useState(null);
  const [editContentSaving, setEditContentSaving] = useState(false);
  const [editContentError, setEditContentError] = useState("");
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [addNoteSaving, setAddNoteSaving] = useState(false);
  const [addNoteError, setAddNoteError] = useState("");
  const [addNoteTarget, setAddNoteTarget] = useState(null);
  const [addNoteTitle, setAddNoteTitle] = useState("Note");
  const [addNoteBody, setAddNoteBody] = useState("");
  const [addTagOpen, setAddTagOpen] = useState(false);
  const [addTagSaving, setAddTagSaving] = useState(false);
  const [addTagError, setAddTagError] = useState("");
  const [addTagTarget, setAddTagTarget] = useState(null);
  const [addTagValue, setAddTagValue] = useState("");

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, severity, message });
  }, []);

  const closeSnackbar = useCallback(
    () => setSnackbar((prev) => ({ ...prev, open: false })),
    []
  );

  const loadSequences = useCallback(async () => {
    setIsLoadingSequences(true);
    setSequencesLoadError("");
    try {
      const rows = await fetchSequences();
      setRowsByTab((prev) => ({
        ...prev,
        sequences: rows.map((row, index) => normalizeSequenceRow(row, index)),
      }));
    } catch (error) {
      setRowsByTab((prev) => ({ ...prev, sequences: [] }));
      setSequencesLoadError(error?.message || "Failed to load sequences.");
    } finally {
      setIsLoadingSequences(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadSequences();
  }, [user, loadSequences]);

  const loadJobPostings = useCallback(async (signal) => {
    setIsLoadingJobs(true);
    setJobsLoadError("");
    try {
      const jobs = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await fetch(
          `${API_BASE}/api/jobs?page=${page}&limit=100&sort=id&order=desc`,
          { credentials: "include", headers: getAuthHeaders(), signal }
        );
        const payload = await readApiJson(response, "Job postings");
        if (!Array.isArray(payload.rows)) throw new Error("Invalid job postings response.");
        jobs.push(...payload.rows);
        totalPages = Number(payload.totalPages) || 1;
        page += 1;
      } while (page <= totalPages);
      if (signal?.aborted) return;
      setRowsByTab((prev) => ({
        ...prev,
        job_postings: jobs.map(createJobRowFromApi),
      }));
    } catch (error) {
      if (signal?.aborted) return;
      setRowsByTab((prev) => ({ ...prev, job_postings: [] }));
      setJobsLoadError(error?.message || "Failed to load job postings.");
    } finally {
      if (!signal?.aborted) setIsLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const controller = new AbortController();
    loadJobPostings(controller.signal);
    return () => controller.abort();
  }, [user, loadJobPostings]);

  const loadContentLibrary = useCallback(async (signal) => {
    setIsLoadingLibrary(true);
    setLibraryLoadError("");
    try {
      const records = await fetchContentLibrary(signal);
      const rows = Object.fromEntries(LIBRARY_TABS.map((tab) => [tab, []]));
      records.forEach((record, index) => {
        const row = createContentRowFromApi(record, index);
        if (!rows[row.tabId]) throw new Error("Invalid library content type.");
        rows[row.tabId].push(row);
      });
      if (signal?.aborted) return;
      setRowsByTab((prev) => ({ ...prev, ...rows }));
    } catch (error) {
      if (signal?.aborted) return;
      setLibraryLoadError(error?.message || "Failed to load content.");
      // Retain previously loaded records if a refresh fails.
    } finally {
      if (!signal?.aborted) setIsLoadingLibrary(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let controller;
    const refresh = () => {
      controller?.abort();
      controller = new AbortController();
      loadContentLibrary(controller.signal);
    };
    refresh();
    window.addEventListener("rpo:content-updated", refresh);
    return () => {
      controller.abort();
      window.removeEventListener("rpo:content-updated", refresh);
    };
  }, [user, loadContentLibrary]);

  useContentRouteTab(location.search, setActiveTab, setContentStatFilterId);

  const isSequencesTab = activeTab === "sequences";
  const activeRows = useMemo(() => rowsByTab[activeTab] || [], [rowsByTab, activeTab]);
  const filterRecords = useMemo(() => Object.values(rowsByTab).flat(), [rowsByTab]);
  const activeColumns = CONTENT_COLUMNS;
  const searchTerms = useMemo(
    () =>
      String(search || "")
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean),
    [search]
  );
  const searchableRows = useMemo(
    () =>
      activeRows.map((row) => ({
        row,
        haystack: buildContentSearchHaystack(row, user),
      })),
    [activeRows, user]
  );

  const tabCounts = useMemo(
    () =>
      CONTENT_TABS.reduce((acc, tab) => {
        acc[tab.id] =
          rowsByTab[tab.id]?.length ?? 0;
        return acc;
      }, {}),
    [rowsByTab]
  );

  const contextRows = useMemo(() => {
    return searchableRows
      .filter(({ row, haystack }) => {
        if (isSequenceRow(row)) {
          if (filterPreset !== "all" && row.statusRaw !== filterPreset) return false;
          if (!searchTerms.length) return true;

          return searchTerms.every((term) => haystack.includes(term));
        }

        if (isTemplateRow(row)) {
          if (filterPreset === "shared" && row.sharing?.type === "link") return false;
          if (filterPreset === "top_opened" && Number(row.opened_rate || 0) < 18) return false;
          if (filterPreset === "recent" && Number(row.last_used_days_ago ?? 999) > 7) return false;
          if (!searchTerms.length) return true;

          return searchTerms.every((term) => haystack.includes(term));
        }

        if (filterPreset === "job_board" && row.source !== "Job Board") return false;
        if (filterPreset === "linkedin" && row.source !== "LinkedIn") return false;
        if (filterPreset === "high_match" && row.best_match_percentage < 80) return false;
        if (
          filterPreset === "active" &&
          ["Closed", "Archived", "Rejected", "Completed"].includes(row.status)
        ) {
          return false;
        }
        if (!searchTerms.length) return true;

        return searchTerms.every((term) => haystack.includes(term));
      })
      .filter(({ row }) =>
        contentStatFilterId
          ? matchesContentStatFilter(row, contentStatFilterId, {
              usageMetricId: contentUsageMetricId,
            })
          : true
      )
      .map(({ row }) => row);
  }, [
    contentStatFilterId,
    contentUsageMetricId,
    filterPreset,
    searchTerms,
    searchableRows,
  ]);

  const filteredRows = useMemo(() => contextRows.filter((row) =>
    matchesRowWithContentFilters(row, user, contentFilters)), [contextRows, user, contentFilters]);

  const selectedRows = useMemo(() => {
    const selected = new Set(selectedIds.map((id) => String(id)));
    return activeRows.filter((row) => selected.has(String(row.id)));
  }, [activeRows, selectedIds]);
  const selectedCount = selectedRows.length;
  const isAdminUser = ADMIN_ROLES.has(String(user?.role || "").trim().toLowerCase());
  const activeContentTypeKey = useMemo(
    () => resolveContentTypeKey(activeTab, activeRows),
    [activeRows, activeTab]
  );
  const activeContentTypeLabel = useMemo(() => {
    const fromRows = activeRows.find((row) => String(row?.content_type_label || "").trim());
    if (fromRows?.content_type_label) return fromRows.content_type_label;
    return titleCaseWords(activeContentTypeKey.replace(/_/g, " "));
  }, [activeContentTypeKey, activeRows]);
  const availableUseContentTypeKeys = useMemo(() => {
    if (selectedRows.length) {
      const selectedTypeKeySet = new Set(
        selectedRows
          .map((row) => resolveRowContentTypeKey(row, activeTab))
          .filter((typeKey) => USE_CONTENT_OPTIONS_BY_TYPE[typeKey])
      );

      return USE_CONTENT_OPTION_TYPE_ORDER.filter((typeKey) =>
        selectedTypeKeySet.has(typeKey)
      );
    }

    return USE_CONTENT_OPTIONS_BY_TYPE[activeContentTypeKey] ? [activeContentTypeKey] : [];
  }, [activeContentTypeKey, activeTab, selectedRows]);
  const availableUseContentOptions = useMemo(
    () => buildUseContentOptions(availableUseContentTypeKeys),
    [availableUseContentTypeKeys]
  );
  const activeStatusOptions = useMemo(
    () => CONTENT_STATUS_OPTIONS_BY_TYPE[activeContentTypeKey] || STATUS_OPTIONS.map((value) => ({ label: value, value })),
    [activeContentTypeKey]
  );
  const activePublishOptions = useMemo(
    () => PUBLISH_OPTIONS_BY_TYPE[activeContentTypeKey] || [],
    [activeContentTypeKey]
  );

  const selectedSequenceItems = useMemo(() => {
    if (isSequencesTab) return [];
    return selectedRows.map((row) => buildJobSequenceItem(row, "content"));
  }, [isSequencesTab, selectedRows]);

  const handleContentUsageMetricChange = useCallback((nextMetricId) => {
    const normalizedMetricId = CONTENT_USAGE_STAT_OPTIONS.some(
      (option) => option.id === nextMetricId
    )
      ? nextMetricId
      : DEFAULT_CONTENT_USAGE_STAT_OPTION_ID;

    setContentUsageMetricId(normalizedMetricId);
    setContentStatFilterId("content_usage");
  }, []);

  const topStats = useMemo(
    () =>
      createTopStats(rowsByTab, {
        contentUsageMetricId,
      }).map((stat) =>
        stat.id === "content_usage"
          ? {
              ...stat,
              menuOptions: CONTENT_USAGE_STAT_OPTIONS.map((option) => ({
                value: option.id,
                label: option.label,
              })),
              onMenuSelect: handleContentUsageMetricChange,
            }
          : stat
      ),
    [contentUsageMetricId, handleContentUsageMetricChange, rowsByTab]
  );

  const resetContentFilters = useCallback(() => {
    const emptyFilters = createDefaultContentFilters();
    setContentFilters(emptyFilters);
    setContentFilterDraft(emptyFilters);
  }, []);

  const resetContentViewState = useCallback(() => {
    setSelectedIds([]);
    setFilterPreset("all");
    setIsFilterDrawerOpen(false);
    setRowMenuAnchorEl(null);
    setRowMenuItem(null);
    setDetailRow(null);
    resetContentFilters();
  }, [resetContentFilters]);

  const handleOpenFilterDrawer = useCallback(() => {
    setContentFilterDraft(normalizeContentFilters(contentFilters));
    setIsFilterDrawerOpen(true);
  }, [contentFilters]);

  const handleCloseFilterDrawer = useCallback(() => {
    setContentFilterDraft(normalizeContentFilters(contentFilters));
    setIsFilterDrawerOpen(false);
  }, [contentFilters]);

  const handleApplyContentFilters = useCallback(
    ({ keepDrawerOpen = false, notify = false } = {}) => {
      const nextFilters = normalizeContentFilters(contentFilterDraft);
      const targetTabId =
        CONTENT_FILTER_TYPE_TO_TAB_ID[normalizeSearchValue(nextFilters.contentType)];

      setContentFilters(nextFilters);

      if (targetTabId && targetTabId !== activeTab && VALID_CONTENT_TAB_IDS.has(targetTabId)) {
        setActiveTab(targetTabId);
        setContentStatFilterId(getPrimaryContentStatIdForTab(targetTabId));
        setSelectedIds([]);
        setRowMenuAnchorEl(null);
        setRowMenuItem(null);
        setDetailRow(null);
      }

      if (!keepDrawerOpen) {
        setIsFilterDrawerOpen(false);
      }

      if (notify) {
        showSnackbar("Advanced content search filters applied.", "info");
      }
    },
    [activeTab, contentFilterDraft, showSnackbar]
  );

  const handleClearContentFilters = useCallback(() => {
    resetContentFilters();
    setFilterPreset("all");
    setIsFilterDrawerOpen(false);
  }, [resetContentFilters]);

  const handleAdvancedContentFilters = useCallback(() => {
    handleApplyContentFilters({ keepDrawerOpen: true, notify: true });
  }, [handleApplyContentFilters]);

  const handleStatsItemClick = useCallback((item) => {
    const statId = String(item?.id || "");
    if (!statId) return;
    const targetTabId = getContentStatTargetTabId(statId, contentUsageMetricId);

    resetContentViewState();

    if (targetTabId && VALID_CONTENT_TAB_IDS.has(targetTabId)) {
      setActiveTab(targetTabId);
    }

    setContentStatFilterId(statId);
  }, [contentUsageMetricId, resetContentViewState]);

  useEffect(() => {
    const validIds = new Set(activeRows.map((row) => String(row.id)));
    setSelectedIds((prev) => prev.filter((id) => validIds.has(String(id))));
  }, [activeRows]);

  useEffect(() => {
    if (!detailRow || detailRow.tabId !== activeTab) return;
    const exists = activeRows.some((row) => String(row.id) === String(detailRow.id));
    if (!exists) setDetailRow(null);
  }, [activeRows, activeTab, detailRow]);

  const deleteRows = useCallback(
    (ids) => {
      const idSet = new Set((ids || []).map((id) => String(id)));
      if (!idSet.size) return;
      setRowsByTab((prev) => ({
        ...prev,
        [activeTab]: (prev[activeTab] || []).filter((row) => !idSet.has(String(row.id))),
      }));
      setSelectedIds((prev) => prev.filter((id) => !idSet.has(String(id))));
    },
    [activeTab]
  );

  const updateRowForActiveTab = useCallback(
    (rowId, mutator) => {
      const targetId = String(rowId || "");
      if (!targetId || typeof mutator !== "function") return;

      let nextRowSnapshot = null;
      setRowsByTab((prev) => {
        const nextRows = (prev[activeTab] || []).map((row, index) => {
          if (String(row.id) !== targetId) return row;
          const nextRow = mutator(row, index);
          nextRowSnapshot = nextRow;
          return nextRow;
        });

        return {
          ...prev,
          [activeTab]: nextRows,
        };
      });

      setDetailRow((prev) =>
        prev && String(prev.id) === targetId && nextRowSnapshot ? nextRowSnapshot : prev
      );
    },
    [activeTab]
  );

  const mutateSelectedRowsForActiveTab = useCallback(
    (mutator) => {
      const selectedIdSet = new Set(selectedIds.map((id) => String(id)));
      setRowsByTab((prev) => ({
        ...prev,
        [activeTab]: (prev[activeTab] || []).map((row, index) =>
          selectedIdSet.has(String(row.id)) ? mutator(row, index) : row
        ),
      }));
    },
    [activeTab, selectedIds]
  );

  const prependRowsForActiveTab = useCallback(
    (newRows) => {
      const safeRows = Array.isArray(newRows) ? newRows.filter(Boolean) : [];
      if (!safeRows.length) return;

      setRowsByTab((prev) => {
        const existingRows = Array.isArray(prev[activeTab]) ? prev[activeTab] : [];
        return {
          ...prev,
          [activeTab]: [...safeRows, ...existingRows].map((row, index) => ({
            ...row,
            sortOrder: index + 1,
          })),
        };
      });
    },
    [activeTab]
  );

  const createContentActivity = useCallback(
    async (row, descriptor = {}, index = 0) => {
      if (!row) {
        throw new Error("A content row is required to create an activity.");
      }

      const resolveText = (value) =>
        typeof value === "function" ? value(row, index) : String(value || "");

      const response = await fetch(`${API_BASE}/api/activities`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({
          entityType: resolveActivityEntityType(row),
          entityId: resolveActivityEntityId(row, index),
          activityType: descriptor.activityType || "action",
          title: resolveText(descriptor.title).trim() || "Content Action",
          summary:
            resolveText(descriptor.summary).trim() ||
            `Action completed for "${getRowPrimaryLabel(row)}".`,
          details:
            resolveText(descriptor.details).trim() ||
            `Action completed for "${getRowPrimaryLabel(row)}".`,
          metadata: {
            bulkAction: descriptor.bulkAction || "",
            bulkOption: descriptor.bulkOption || "",
            contentType: row?.content_type_key || row?.contentType || activeContentTypeKey,
            contentName: getRowPrimaryLabel(row),
            ...(descriptor.metadata && typeof descriptor.metadata === "object"
              ? descriptor.metadata
              : {}),
          },
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to create activity (${response.status})`);
      }

      return payload;
    },
    [activeContentTypeKey]
  );

  const logBulkActionActivities = useCallback(async (rows, descriptor = {}) => {
    const safeRows = (Array.isArray(rows) ? rows : []).filter(Boolean);
    if (!safeRows.length) {
      return { created: 0, failed: 0 };
    }

    const resolveText = (value, row, index) =>
      typeof value === "function" ? value(row, index) : String(value || "");

    const results = await Promise.allSettled(
      safeRows.map((row, index) =>
        createContentActivity(row, {
          ...descriptor,
          title: resolveText(descriptor.title, row, index),
          summary: resolveText(descriptor.summary, row, index),
          details: resolveText(descriptor.details, row, index),
        }, index)
      )
    );

    return results.reduce(
      (acc, result) => {
        if (result.status === "fulfilled") {
          acc.created += 1;
        } else {
          acc.failed += 1;
        }
        return acc;
      },
      { created: 0, failed: 0 }
    );
  }, [createContentActivity]);

  const runBulkAction = useCallback(
    async ({
      rows = selectedRows,
      emptyMessage = `Select at least one ${activeContentTypeLabel.toLowerCase()} first.`,
      allowEmpty = false,
      execute,
      title,
      summary,
      details,
      bulkAction = "",
      bulkOption = "",
      metadata = {},
      successMessage = "Bulk action completed.",
      successSeverity = "success",
      failureMessage = "Bulk action failed.",
      logBeforeExecute = false,
    } = {}) => {
      const safeRows = (Array.isArray(rows) ? rows : []).filter(Boolean);
      if (!safeRows.length && !allowEmpty) {
        showSnackbar(emptyMessage, "warning");
        return false;
      }

      try {
        let activityResult = { created: 0, failed: 0 };

        if (logBeforeExecute) {
          activityResult = await logBulkActionActivities(safeRows, {
            title,
            summary,
            details,
            bulkAction,
            bulkOption,
            metadata,
          });
        }

        const libraryMutation = LIBRARY_TABS.includes(activeTab)
          ? contentMutationForAction(bulkAction, metadata) : null;
        if (libraryMutation) {
          await mutateContentLibrary(activeContentTypeKey, safeRows.map((row) => row.id), libraryMutation.action, libraryMutation.changes);
          await loadContentLibrary();
          setSelectedIds([]);
          setDetailRow(null);
        } else if (typeof execute === "function") {
          const result = execute(safeRows);
          if (result && typeof result.then === "function") {
            await result;
          }
        }

        if (!logBeforeExecute) {
          activityResult = await logBulkActionActivities(safeRows, {
            title,
            summary,
            details,
            bulkAction,
            bulkOption,
            metadata,
          });
        }

        const nextSeverity = activityResult.failed ? "warning" : successSeverity;
        const activitySuffix = activityResult.failed
          ? ` Activity logging failed for ${activityResult.failed} ${activityResult.failed === 1 ? "item" : "items"}.`
          : "";
        showSnackbar(`${successMessage}${activitySuffix}`, nextSeverity);
        return true;
      } catch (error) {
        console.error("Bulk content action failed:", error);
        showSnackbar(error?.message || failureMessage, "error");
        return false;
      }
    },
    [activeTab, activeContentTypeKey, activeContentTypeLabel, loadContentLibrary, logBulkActionActivities, selectedRows, showSnackbar]
  );

  const handleAssignOwner = useCallback(
    async (member) => {
      const ownerName = String(member?.name || "").trim();
      if (!ownerName) return;

      await runBulkAction({
        title: "Assign Owner",
        summary: `Assigned ${ownerName} as content owner.`,
        details: (row) => `Assigned ${ownerName} as owner for "${getRowPrimaryLabel(row)}".`,
        bulkAction: "assign_owner",
        metadata: {
          ownerName,
          ownerUserId: member?.id ?? null,
        },
        successMessage: `Assigned owner for ${selectedCount} selected ${selectedCount === 1 ? "item" : "items"} to ${ownerName}.`,
        failureMessage: "Failed to assign content owner.",
        execute: () => {
          const ownerInitials = getAvatarLetters(ownerName, "NA");
          mutateSelectedRowsForActiveTab((row) => ({
            ...row,
            owner: ownerName,
            owner_name: ownerName,
            owner_initials: ownerInitials,
          }));
        },
      });
    },
    [mutateSelectedRowsForActiveTab, runBulkAction, selectedCount]
  );

  const handleUseContentAction = useCallback(
    async (optionValue) => {
      const option = availableUseContentOptions.find(
        (item) => item?.value === optionValue || item?.label === optionValue
      );
      if (!option) return;

      const optionContentTypeKey = String(option.contentTypeKey || "").trim();
      const compatibleRows = selectedRows.filter(
        (row) => resolveRowContentTypeKey(row, activeTab) === optionContentTypeKey
      );
      const compatibleCount = compatibleRows.length;

      if (!compatibleCount) {
        showSnackbar("No compatible content is selected for that action.", "warning");
        return;
      }

      await runBulkAction({
        rows: compatibleRows,
        emptyMessage: `Select at least one ${titleCaseWords(
          optionContentTypeKey.replace(/_/g, " ")
        ).toLowerCase()} first.`,
        title: "Use Content",
        summary: `${option.label} launched from selected content.`,
        details: (row) => `${option.label} launched for "${getRowPrimaryLabel(row)}".`,
        bulkAction: "use_content",
        bulkOption: option.value,
        metadata: {
          contentType: optionContentTypeKey,
          optionLabel: option.label,
        },
        successMessage: `${option.label} is ready for ${compatibleCount} selected ${compatibleCount === 1 ? "item" : "items"}.`,
        failureMessage: `Failed to start ${option.label.toLowerCase()}.`,
      });
    },
    [
      activeTab,
      availableUseContentOptions,
      runBulkAction,
      selectedRows,
      showSnackbar,
    ]
  );

  const handleDuplicateSelection = useCallback(async () => {
    await runBulkAction({
      title: "Duplicate Content",
      summary: `Duplicated ${activeContentTypeLabel.toLowerCase()} content.`,
      details: (row) => `Duplicated "${getRowPrimaryLabel(row)}".`,
      bulkAction: "duplicate_content",
      metadata: {
        contentType: activeContentTypeKey,
      },
      successMessage: `Duplicated ${selectedCount} selected ${selectedCount === 1 ? "item" : "items"}.`,
      failureMessage: "Failed to duplicate content.",
      execute: async (rows) => {
        if (isSequencesTab) {
          await Promise.all(
            rows.map(async (row) => {
              const sequenceId = row.sequenceId || row.id;
              const payload = await fetchSequenceById(sequenceId);
              const sequence = payload?.sequence || payload;
              const baseName =
                String(sequence?.name || row.content_name || "Sequence").trim() || "Sequence";

              await createSequence({
                sequence: {
                  ...sequence,
                  name: `${baseName} (Copy)`,
                  status: "draft",
                },
                steps: Array.isArray(sequence?.steps) ? sequence.steps : [],
                enrollments: Array.isArray(sequence?.enrollments)
                  ? sequence.enrollments
                  : [],
              });
            })
          );
          await loadSequences();
          return;
        }

        const timestamp = Date.now();
        const duplicates = rows.map((row, index) => {
          const nextTimestamp = timestamp + index;
          const nextName = `${getRowPrimaryLabel(row)} (Copy)`;
          const nextIso = new Date(nextTimestamp).toISOString();
          return {
            ...row,
            id: `${activeTab}-copy-${nextTimestamp}`,
            content_name: nextName,
            job_title: nextName,
            name: row?.name ? nextName : row?.name,
            status: "Draft",
            statusRaw: "draft",
            last_used_at: nextIso,
            last_used_label: formatDateLabel(nextIso),
            last_used_sort: -Date.parse(nextIso),
            updatedAtSort: -Date.parse(nextIso),
          };
        });

        prependRowsForActiveTab(duplicates);
      },
    });
  }, [
    activeContentTypeKey,
    activeContentTypeLabel,
    activeTab,
    isSequencesTab,
    loadSequences,
    prependRowsForActiveTab,
    runBulkAction,
    selectedCount,
  ]);

  const handleApplyStatus = useCallback(
    async (status) => {
      const nextStatus = String(status || "").trim();
      if (!nextStatus) return;

      await runBulkAction({
        title: "Change Status",
        summary: `Changed status to ${nextStatus}.`,
        details: (row) => `Changed status for "${getRowPrimaryLabel(row)}" to ${nextStatus}.`,
        bulkAction: "change_status",
        bulkOption: nextStatus,
        metadata: {
          nextStatus,
          contentType: activeContentTypeKey,
        },
        successMessage: `Status updated to "${nextStatus}" for ${selectedCount} selected ${selectedCount === 1 ? "item" : "items"}.`,
        failureMessage: "Failed to change content status.",
        execute: () => {
          mutateSelectedRowsForActiveTab((row) => ({
            ...row,
            status: nextStatus,
            statusRaw: nextStatus.toLowerCase(),
          }));
        },
      });
    },
    [activeContentTypeKey, mutateSelectedRowsForActiveTab, runBulkAction, selectedCount]
  );

  const handlePublishAction = useCallback(
    async (optionValue) => {
      const option = activePublishOptions.find(
        (item) => item?.value === optionValue || item?.label === optionValue
      );
      if (!option) return;

      const publishStatus = PUBLISH_STATUS_BY_TYPE[activeContentTypeKey] || "Active";
      await runBulkAction({
        title: "Publish Content",
        summary: `${option.label} started.`,
        details: (row) => `${option.label} triggered for "${getRowPrimaryLabel(row)}".`,
        bulkAction: "publish_content",
        bulkOption: option.value,
        metadata: {
          publishStatus,
          contentType: activeContentTypeKey,
        },
        successMessage: `${option.label} completed for ${selectedCount} selected ${selectedCount === 1 ? "item" : "items"}.`,
        failureMessage: "Failed to publish content.",
        execute: () => {
          mutateSelectedRowsForActiveTab((row) => ({
            ...row,
            status: publishStatus,
            statusRaw: publishStatus.toLowerCase(),
          }));
        },
      });
    },
    [
      activeContentTypeKey,
      activePublishOptions,
      mutateSelectedRowsForActiveTab,
      runBulkAction,
      selectedCount,
    ]
  );

  const handleExport = useCallback(
    async (format = "csv") => {
      const exportFormat = String(format || "").trim().toLowerCase();
      const rows = selectedRows;
      const formatLabel = exportFormat === "docx" ? "DOCX" : exportFormat.toUpperCase();

      await runBulkAction({
        rows,
        title: "Export Content",
        summary: `Exported content as ${formatLabel}.`,
        details: (row) => `Exported "${getRowPrimaryLabel(row)}" as ${formatLabel}.`,
        bulkAction: "export_content",
        bulkOption: exportFormat,
        metadata: {
          format: exportFormat,
          contentType: activeContentTypeKey,
        },
        successMessage:
          exportFormat === "csv"
            ? `Exported ${rows.length} selected ${rows.length === 1 ? "item" : "items"} as CSV.`
            : `${formatLabel} export is ready for the next workflow for ${rows.length} selected ${rows.length === 1 ? "item" : "items"}.`,
        failureMessage: `Failed to export content as ${formatLabel}.`,
        execute:
          exportFormat === "csv"
            ? (safeRows) => {
                const csv = [
                  [
                    "Content Name",
                    "Content Type",
                    "Status",
                    "Related Entity",
                    "Usage",
                    "Owner",
                    "Last Used",
                    "Performance",
                  ].join(","),
                  ...safeRows.map((row) =>
                    [
                      row.content_name,
                      row.content_type_label,
                      row.status,
                      row.related_entity_label,
                      row.usage_label,
                      row.owner_name,
                      row.last_used_label,
                      `${row.performance_label}: ${row.performance_display}`,
                    ]
                      .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
                      .join(",")
                  ),
                ].join("\n");

                downloadCsv(`${activeTab}-${Date.now()}.csv`, csv);
              }
            : undefined,
      });
    },
    [activeContentTypeKey, activeTab, runBulkAction, selectedRows]
  );

  const handleArchiveSelection = useCallback(async () => {
    await runBulkAction({
      title: "Archive Content",
      summary: "Archived selected content.",
      details: (row) => `Archived "${getRowPrimaryLabel(row)}".`,
      bulkAction: "archive_content",
      metadata: {
        contentType: activeContentTypeKey,
      },
      successMessage: `Archived ${selectedCount} selected ${selectedCount === 1 ? "item" : "items"}.`,
      failureMessage: "Failed to archive content.",
      execute: () => {
        mutateSelectedRowsForActiveTab((row) => ({
          ...row,
          status: "Archived",
          statusRaw: "archived",
        }));
      },
    });
  }, [activeContentTypeKey, mutateSelectedRowsForActiveTab, runBulkAction, selectedCount]);

  const handleBulkDelete = useCallback(
    async (_event, options = {}) => {
      if (!isAdminUser) {
        showSnackbar("Only admin users can delete content.", "error");
        return;
      }

      const rows = selectedRows;
      if (!rows.length) {
        showSnackbar(
          `Select at least one ${activeContentTypeLabel.toLowerCase()} first.`,
          "warning"
        );
        return;
      }

      if (
        !options?.confirmedByBulkActionsBar &&
        !window.confirm(
          `Delete ${rows.length} selected ${rows.length === 1 ? "item" : "items"}? This cannot be undone.`
        )
      ) {
        return;
      }

      await runBulkAction({
        rows,
        title: "Delete Content",
        summary: "Deleted selected content.",
        details: (row) => `Deleted "${getRowPrimaryLabel(row)}".`,
        bulkAction: "delete_content",
        metadata: {
          contentType: activeContentTypeKey,
        },
        successMessage: `Deleted ${rows.length} selected ${rows.length === 1 ? "item" : "items"}.`,
        failureMessage: "Failed to delete content.",
        logBeforeExecute: true,
        execute: (safeRows) => deleteRows(safeRows.map((row) => row.id)),
      });
    },
    [
      activeContentTypeKey,
      activeContentTypeLabel,
      deleteRows,
      isAdminUser,
      runBulkAction,
      selectedRows,
      showSnackbar,
    ]
  );

  const handleLaunchAgent = useCallback(async () => {
    await runBulkAction({
      allowEmpty: true,
      title: "Launch Agent",
      summary: `Launched agent for ${activeContentTypeLabel}.`,
      details: (row) => `Launched agent for "${getRowPrimaryLabel(row)}".`,
      bulkAction: "launch_agent",
      metadata: {
        contentType: activeContentTypeKey,
      },
      successMessage: selectedCount
        ? `Launch Agent is ready for ${selectedCount} selected ${selectedCount === 1 ? "item" : "items"}.`
        : "Launch Agent is ready.",
      failureMessage: "Failed to launch agent for selected content.",
    });
  }, [activeContentTypeKey, activeContentTypeLabel, runBulkAction, selectedCount]);

  const handleOpenSequenceEditor = useCallback(
    async (row) => {
      if (!row) return;

      const sequenceId = row.sequenceId || row.id;

      try {
        const payload = await fetchSequenceById(sequenceId);
        const sequence = payload?.sequence || payload;
        setDetailRow(null);
        setEditSequence(sequence);
        setIsAddToSequenceOpen(true);
      } catch (error) {
        showSnackbar(error?.message || "Failed to load sequence.", "error");
      }
    },
    [showSnackbar]
  );

  const closeAddNoteDialog = useCallback(() => {
    if (addNoteSaving) return;
    setAddNoteOpen(false);
    setAddNoteError("");
    setAddNoteTarget(null);
    setAddNoteTitle("Note");
    setAddNoteBody("");
  }, [addNoteSaving]);

  const closeAddTagDialog = useCallback(() => {
    if (addTagSaving) return;
    setAddTagOpen(false);
    setAddTagError("");
    setAddTagTarget(null);
    setAddTagValue("");
  }, [addTagSaving]);

  const closeRowMenu = useCallback(() => {
    setRowMenuAnchorEl(null);
    setRowMenuItem(null);
  }, []);

  const openLibraryDetails = useCallback((row) => {
    navigate(`/content?tab=${encodeURIComponent(row.tabId)}&contentId=${encodeURIComponent(row.id)}&view=details`);
  }, [navigate]);

  const handleViewRowAction = useCallback(() => {
    if (rowMenuItem) {
      if (LIBRARY_TABS.includes(rowMenuItem.tabId)) openLibraryDetails(rowMenuItem);
      else setDetailRow(rowMenuItem);
    }
    closeRowMenu();
  }, [closeRowMenu, rowMenuItem, openLibraryDetails]);

  const handleEditRowAction = useCallback(() => {
    const targetRow = rowMenuItem;
    closeRowMenu();
    if (!targetRow) return;

    if (isSequenceRow(targetRow)) {
      handleOpenSequenceEditor(targetRow);
      return;
    }

    if (LIBRARY_TABS.includes(targetRow.tabId)) {
      setEditContentError("");
      setEditContent({ ...targetRow, title: targetRow.content_name, tags: (targetRow.tags || []).join(", "), owner_name: targetRow.owner_name === "Unassigned" ? "" : targetRow.owner_name });
      return;
    }

    if (targetRow.tabId === "job_postings") {
      navigate(`/jobs?jobId=${encodeURIComponent(targetRow.jobId || targetRow.id)}`);
      return;
    }

    showSnackbar(`Edit for "${getRowPrimaryLabel(targetRow)}" is ready for the next workflow.`, "info");
  }, [closeRowMenu, handleOpenSequenceEditor, rowMenuItem, showSnackbar, navigate]);

  const handleShareRowAction = useCallback(async () => {
    const targetRow = rowMenuItem;
    closeRowMenu();
    if (!targetRow) return;

    try {
      await createAndCopyPublicShareLink({
        apiBase: API_BASE,
        payload: buildPublicSharePayload("content", targetRow),
      });
      showSnackbar(SHARE_LINK_COPIED_MESSAGE);
    } catch (error) {
      console.error("Failed to share content row:", error);
      showSnackbar(error?.message || "Failed to create share link.", "error");
    }
  }, [closeRowMenu, rowMenuItem, showSnackbar]);

  const handleAddRowNotesAction = useCallback(() => {
    const targetRow = rowMenuItem;
    closeRowMenu();
    if (!targetRow) return;
    setAddNoteError("");
    setAddNoteTarget(targetRow);
    setAddNoteTitle(getRowNoteTitle(targetRow));
    setAddNoteBody(getRowNoteBody(targetRow));
    setAddNoteOpen(true);
  }, [closeRowMenu, rowMenuItem]);

  const handleAddRowTagsAction = useCallback(() => {
    const targetRow = rowMenuItem;
    closeRowMenu();
    if (!targetRow) return;
    setAddTagError("");
    setAddTagTarget(targetRow);
    setAddTagValue(getRowTags(targetRow).join(", "));
    setAddTagOpen(true);
  }, [closeRowMenu, rowMenuItem]);

  const handleSaveAddNote = useCallback(async () => {
    const targetRow = addNoteTarget;
    const noteTitle = String(addNoteTitle || "").trim() || "Note";
    const noteBody = String(addNoteBody || "").trim();

    if (!targetRow) {
      setAddNoteError("Select a content item before adding a note.");
      return;
    }

    if (!noteBody) {
      setAddNoteError("Note text is required.");
      return;
    }

    setAddNoteSaving(true);
    setAddNoteError("");

    try {
      if (LIBRARY_TABS.includes(targetRow.tabId)) {
        await mutateContentLibrary(targetRow.content_type_key, [targetRow.id], "update", { note_title: noteTitle, note_body: noteBody });
      }
      await createContentActivity(targetRow, {
        activityType: "note",
        title: noteTitle,
        summary: noteBody,
        details: noteBody,
        bulkAction: "add_note",
        metadata: {
          contentType: targetRow?.content_type_key || targetRow?.contentType || activeContentTypeKey,
        },
      });

      const noteTimestamp = new Date().toISOString();
      updateRowForActiveTab(targetRow.id, (row) => ({
        ...row,
        latest_note_title: noteTitle,
        latest_note_body: noteBody,
        note_title: noteTitle,
        note_body: noteBody,
        notes: noteBody,
        notes_updated_at: noteTimestamp,
      }));

      closeAddNoteDialog();
      showSnackbar(`Added a note to "${getRowPrimaryLabel(targetRow)}".`);
    } catch (error) {
      setAddNoteError(error?.message || "Failed to add note.");
    } finally {
      setAddNoteSaving(false);
    }
  }, [
    activeContentTypeKey,
    addNoteBody,
    addNoteTarget,
    addNoteTitle,
    closeAddNoteDialog,
    createContentActivity,
    showSnackbar,
    updateRowForActiveTab,
  ]);

  const handleSaveAddTags = useCallback(async () => {
    const targetRow = addTagTarget;
    const tagList = getRowTags({ tags: addTagValue });

    if (!targetRow) {
      setAddTagError("Select a content item before updating tags.");
      return;
    }

    setAddTagSaving(true);
    setAddTagError("");

    try {
      if (LIBRARY_TABS.includes(targetRow.tabId)) {
        await mutateContentLibrary(targetRow.content_type_key, [targetRow.id], "update", { tags: tagList });
      }
      await createContentActivity(targetRow, {
        activityType: "action",
        title: "Tags Updated",
        summary: tagList.length
          ? `Updated tags: ${tagList.join(", ")}`
          : "Cleared tags.",
        details: tagList.length
          ? `Updated tags for "${getRowPrimaryLabel(targetRow)}": ${tagList.join(", ")}`
          : `Cleared tags for "${getRowPrimaryLabel(targetRow)}".`,
        bulkAction: "update_tags",
        metadata: {
          contentType: targetRow?.content_type_key || targetRow?.contentType || activeContentTypeKey,
          tags: tagList,
        },
      });

      updateRowForActiveTab(targetRow.id, (row) => ({
        ...row,
        tags: tagList,
        tag_list: tagList,
        tagList: tagList,
        content_tags: tagList,
        contentTags: tagList,
      }));

      closeAddTagDialog();
      showSnackbar(
        tagList.length
          ? `Updated tags for "${getRowPrimaryLabel(targetRow)}".`
          : `Cleared tags for "${getRowPrimaryLabel(targetRow)}".`
      );
    } catch (error) {
      setAddTagError(error?.message || "Failed to update tags.");
    } finally {
      setAddTagSaving(false);
    }
  }, [
    activeContentTypeKey,
    addTagTarget,
    addTagValue,
    closeAddTagDialog,
    createContentActivity,
    showSnackbar,
    updateRowForActiveTab,
  ]);

  const topBarActions = useMemo(
    () => [
      {
        key: "filter",
        label: "Filter By",
        variant: "contained",
        startIcon: <FilterListRoundedIcon sx={{ fontSize: 16 }} />,
        onClick: handleOpenFilterDrawer,
        sx: {
          boxShadow: "none",
        },
      },
      {
        key: "import-csv",
        label: "Import CSV",
        variant: "contained",
        onClick: () => setCsvImportOpen(true),
        sx: { boxShadow: "none" },
      },
      {
        key: "launch-agent",
        label: "Launch Agent",
        variant: "contained",
        startIcon: <AgentActionIcon size={16} />,
        onClick: handleLaunchAgent,
        sx: { boxShadow: "none" },
      },
    ],
    [handleLaunchAgent, handleOpenFilterDrawer, setCsvImportOpen]
  );

  const bulkActionsNode = useMemo(() => {
    return (
      <BulkActionsBar
        selectedCount={selectedCount}
        selectionLabel={activeContentTypeLabel.toLowerCase()}
        actions={[
          {
            type: "menu",
            label: "Use Content",
            options: availableUseContentOptions,
            onSelect: handleUseContentAction,
            confirmTitle: "Confirm Use Content",
            confirmMessage: ({ optionLabel }) =>
              `Continue with "${optionLabel || "Use Content"}" for the compatible selected content?`,
            disabled: !selectedCount || !availableUseContentOptions.length,
            disabledMessage: !availableUseContentOptions.length
              ? "Use Content is not available for the current selection yet."
              : "Select at least one content item first.",
          },
          { type: "divider", sx: { width: "1.5px" } },
          {
            type: "node",
            key: "assign-owner",
            node: (
              <AssignTeamMemberAction
                apiBase={API_BASE}
                selectedCount={selectedCount}
                label="Assign Owner"
                optionsMode="recruiterUsers"
                assignmentRoleLabel="owner"
                onAssign={handleAssignOwner}
              />
            ),
          },
          { type: "divider" },
          {
            type: "button",
            label: "Duplicate",
            onClick: handleDuplicateSelection,
            disabled: !selectedCount,
          },
          { type: "divider" },
          {
            type: "menu",
            label: "Change Status",
            options: activeStatusOptions,
            onSelect: handleApplyStatus,
            disabledMessage: "Select at least one content item first.",
            disabled: !selectedCount,
          },
          { type: "divider" },
          {
            type: "menu",
            label: "Publish",
            options: activePublishOptions,
            onSelect: handlePublishAction,
            disabled: !selectedCount || !activePublishOptions.length,
            disabledMessage: !activePublishOptions.length
              ? "Publish is available only for supported content types."
              : "Select at least one content item first.",
          },
          { type: "divider" },
          {
            type: "menu",
            label: "Export",
            options: EXPORT_FORMAT_OPTIONS,
            onSelect: handleExport,
            disabledMessage: "Select at least one content item first.",
            disabled: !selectedCount,
          },
          { type: "divider" },
          {
            type: "button",
            label: "Archive",
            onClick: handleArchiveSelection,
            disabled: !selectedCount,
          },
          ...(isAdminUser
            ? [
                { type: "divider" },
                {
                  type: "button",
                  label: "Delete",
                  onClick: handleBulkDelete,
                  disabled: !selectedCount,
                  confirmTitle: "Confirm Delete",
                  confirmMessage: `Delete ${selectedCount} selected ${selectedCount === 1 ? "item" : "items"}? This cannot be undone.`,
                  confirmLabel: "Delete",
                },
              ]
            : []),
        ]}
        rightButton={{
          label: "Launch Agent",
          confirm: false,
          onClick: handleLaunchAgent,
          requiresSelection: false,
          sx: {
            ...toolbarPillButtonSx,
            "@media (max-width:1300px)": { display: "none" },
          },
        }}
      />
    );
  }, [
    activeContentTypeLabel,
    activePublishOptions,
    activeStatusOptions,
    availableUseContentOptions,
    handleArchiveSelection,
    handleApplyStatus,
    handleBulkDelete,
    handleDuplicateSelection,
    handleExport,
    handleLaunchAgent,
    handlePublishAction,
    handleUseContentAction,
    handleAssignOwner,
    isAdminUser,
    selectedCount,
  ]);

  const renderRow = useCallback(
    (row) => {
      const ownerInitials = row.owner_initials || getAvatarLetters(row.owner_name, "NA");
      const contentDescription =
        row.content_description || row.description || "No description yet.";

      return (
        <>
          <TableCell>
            <Box sx={{ minWidth: 0, maxWidth: 280 }}>
              {(isSequenceRow(row) || LIBRARY_TABS.includes(row.tabId)) ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => isSequenceRow(row) ? handleOpenSequenceEditor(row) : openLibraryDetails(row)}
                  sx={{
                    display: "block",
                    p: 0,
                    m: 0,
                    border: 0,
                    background: "transparent",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <Typography data-popup-section
                    noWrap
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: BLUE,
                      maxWidth: 270,
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {row.content_name}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  noWrap
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: TEXT,
                    maxWidth: 270,
                  }}
                >
                  {row.content_name}
                </Typography>
              )}
              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: 12,
                  color: "#667085",
                  lineHeight: 1.45,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  maxWidth: 280,
                }}
              >
                {contentDescription}
              </Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Typography variant="body2" sx={{ color: "#475467" }}>
              {row.content_type_label || "Content"}
            </Typography>
          </TableCell>
          <TableCell>
            <Chip label={row.status || "Draft"} size="small" sx={getStatusChipSx(row.status)} />
          </TableCell>
          <TableCell>
            <Chip
              label={row.related_entity_label || "Lead"}
              size="small"
              variant="outlined"
              sx={getRelatedEntityChipSx()}
            />
          </TableCell>
          <TableCell>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                lineHeight: 1.2,
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT }}>
                {row.usage_value ?? "--"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#667085", maxWidth: 120 }}>
                {row.usage_context || "Used"}
              </Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Avatar
                src={row.owner_avatar_url || undefined}
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: row.owner_avatar_url ? "transparent" : "#E0F2FE",
                  color: BLUE,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {!row.owner_avatar_url ? ownerInitials : null}
              </Avatar>
              <Typography
                noWrap
                sx={{ fontSize: 14, fontWeight: 600, color: TEXT, maxWidth: 150 }}
              >
                {row.owner_name || "Unassigned"}
              </Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Typography sx={{ fontSize: 14, color: "#667085" }}>
              {row.last_used_label || "-"}
            </Typography>
          </TableCell>
          <TableCell>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                lineHeight: 1.2,
              }}
            >
              <Typography data-popup-section sx={{ fontSize: 14, fontWeight: 700, color: BLUE }}>
                {row.performance_display || "--"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#667085", maxWidth: 120 }}>
                {row.performance_label || "Performance"}
              </Typography>
            </Box>
          </TableCell>
          <TableCell align="right">
            <IconButton
              size="small"
              onClick={(event) => {
                setRowMenuAnchorEl(event.currentTarget);
                setRowMenuItem(row);
              }}
              sx={{ width: 28, height: 28, color: "#98A2B3" }}
            >
              <MoreVertIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </TableCell>
        </>
      );
    },
    [handleOpenSequenceEditor, openLibraryDetails]
  );

  if (loading) {
    return <p>Loading...</p>;
  }

  const detailParams = new URLSearchParams(location.search);
  const detailCategory = detailParams.get("tab");
  const detailId = detailParams.get("contentId");
  if (detailId && LIBRARY_TABS.includes(detailCategory)) {
    const backToLibrary = () => navigate(`/content?tab=${detailCategory}`);
    const row = (rowsByTab[detailCategory] || []).find((item) => String(item.id) === detailId);
    if (!row) return <Box sx={{ p: 3 }}>
      <Button onClick={backToLibrary}>Back to {findTabLabel(CONTENT_TABS, detailCategory)}</Button>
      {isLoadingLibrary ? <LinearProgress aria-label="Loading content details" /> : <Alert severity="error">{libraryLoadError || "This content could not be found. It may have been deleted."}</Alert>}
      {libraryLoadError && <Button onClick={() => loadContentLibrary()}>Retry</Button>}
    </Box>;
    const requestedView = detailParams.get("view");
    const detailTab = CONTENT_DETAIL_TABS.some((item) => item.value === requestedView) ? requestedView : "details";
    return <ContentDetailsView key={`${detailCategory}-${detailId}`} row={row}
      categoryLabel={findTabLabel(CONTENT_TABS, detailCategory)} tab={detailTab} onBack={backToLibrary}
      statusOptions={CONTENT_STATUS_OPTIONS_BY_TYPE[row.content_type_key] || []}
      sources={filterRecords.map((item) => item.source).filter(Boolean)}
      onTabChange={(view) => { detailParams.set("view", view); navigate(`/content?${detailParams.toString()}`); }}
      onUpdated={(changes) => setRowsByTab((prev) => ({ ...prev, [detailCategory]: prev[detailCategory].map((item) =>
        String(item.id) === detailId ? createContentRowFromApi({ ...item, ...changes, owner: changes.owner_name ?? item.owner, content_type: item.content_type_key }, item.sortOrder - 1, detailCategory) : item) }))}
    />;
  }

  const activeContentTabLabel = findTabLabel(CONTENT_TABS, activeTab);

  return (
    <Box sx={{ py: 0, pb: 3, border: 0 }}>
      <ListTableTabsTemplate
        breadcrumb={
          <Breadcrumb
            activeLabel={activeContentTabLabel}
            items={[
              { label: "Overview", onClick: () => navigate("/overview") },
              { label: "Content", path: "/content" },
            ]}
          />
        }
        statsSection={
          <StatsSlider
            items={topStats}
            autoPlay={false}
            showArrows={true}
            cardMinWidth={200}
            gap={12}
            onItemClick={handleStatsItemClick}
            activeItemId={contentStatFilterId}
          />
        }
        contentSection={
          <>
            <Box sx={{ backgroundColor: "#fff", boxShadow: "none", overflow: "hidden" }}>
              <TopListHeaderBar
                title="Content"
                searchPlaceholder="Search all content..."
                searchValue={search}
                onSearchChange={(event) => setSearch(event.target.value)}
                actions={topBarActions}
                resultsToolbarTargetId="content-results-toolbar"
              />

              <TabsBar
                activeTab={activeTab}
                counts={tabCounts}
                onChange={(tabId) => {
                  setActiveTab(tabId);
                  setContentStatFilterId(getPrimaryContentStatIdForTab(tabId));
                  setSelectedIds([]);
                  setFilterPreset("all");
                  resetContentFilters();
                  setIsFilterDrawerOpen(false);
                  setRowMenuAnchorEl(null);
                  setRowMenuItem(null);
                  setDetailRow(null);
                }}
              />

              {isSequencesTab && sequencesLoadError ? (
                <Alert severity="error" action={<Button onClick={() => loadSequences()}>Retry</Button>}>
                  {sequencesLoadError}
                </Alert>
              ) : null}
              {activeTab === "job_postings" && jobsLoadError ? (
                <Alert severity="error" action={<Button onClick={() => loadJobPostings()}>Retry</Button>}>
                  {jobsLoadError}
                </Alert>
              ) : null}
              {LIBRARY_TABS.includes(activeTab) && libraryLoadError ? (
                <Alert severity="error" action={<Button onClick={() => loadContentLibrary()}>Retry</Button>}>
                  {libraryLoadError}
                </Alert>
              ) : null}
              {(isSequencesTab && isLoadingSequences) || (activeTab === "job_postings" && isLoadingJobs) || (LIBRARY_TABS.includes(activeTab) && isLoadingLibrary) ? (
                <LinearProgress
                  sx={{
                    height: 2,
                    backgroundColor: "#F5F3FF",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: PURPLE,
                    },
                  }}
                />
              ) : null}

              <GenericDataTable
                key={activeTab}
                columns={activeColumns}
                exportFields={[...activeColumns, ...CONTENT_EXPORT_TAB_FIELDS].filter((field) => field.id !== "actions")}
                rows={filteredRows}
                renderRow={renderRow}
                includeSelectionColumn={true}
                selectedIds={selectedIds}
                onSelectedIdsChange={(ids) => setSelectedIds(Array.isArray(ids) ? ids.map((id) => String(id)) : [])}
                bulkActionsTop={bulkActionsNode}
                bulkActionsBottom={bulkActionsNode}
                initialRowsPerPage={10}
                defaultOrderBy="last_used_sort"
                resultsToolbarTargetId="content-results-toolbar"
              />
            </Box>

            <ContentFiltersDrawer
              open={isFilterDrawerOpen}
              onClose={handleCloseFilterDrawer}
              draft={contentFilterDraft}
              setDraft={setContentFilterDraft}
              endpoint={`/api/content:${activeTab}?${new URLSearchParams({search, preset:filterPreset, stats_filter:contentStatFilterId || ""})}`}
              onClear={handleClearContentFilters}
              onApply={() => handleApplyContentFilters()}
              onAdvancedSearch={handleAdvancedContentFilters}
            />

            <Menu
              open={Boolean(rowMenuAnchorEl)}
              anchorEl={rowMenuAnchorEl}
              onClose={closeRowMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: { borderRadius: 2, minWidth: 230, boxShadow: "0 16px 40px rgba(15,23,42,0.16)" } }}
            >
              <MenuItem onClick={handleViewRowAction} sx={{ fontSize: 13 }}>
                View
              </MenuItem>
              <MenuItem onClick={handleShareRowAction} sx={{ fontSize: 13 }}>
                Share Link
              </MenuItem>
              <MenuItem onClick={handleEditRowAction} sx={{ fontSize: 13 }}>
                Edit
              </MenuItem>
              <MenuItem onClick={handleAddRowNotesAction} sx={{ fontSize: 13 }}>
                Add Notes
              </MenuItem>
              <MenuItem onClick={handleAddRowTagsAction} sx={{ fontSize: 13 }}>
                Add Tags
              </MenuItem>
            </Menu>

            <DetailsDrawer open={Boolean(detailRow)} row={detailRow} onClose={() => setDetailRow(null)} />

            <Dialog open={Boolean(editContent)} onClose={() => !editContentSaving && setEditContent(null)} fullWidth maxWidth="md">
              <DialogTitle data-popup-header>Edit {editContent?.content_type_label || "Content"}</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  {editContentError ? <Alert severity="error">{editContentError}</Alert> : null}
                  {editContent && <ContentEditorFields draft={editContent} onChange={setEditContent} disabled={editContentSaving} sources={filterRecords.map((item) => item.source).filter(Boolean)} />}
                </Stack>
              </DialogContent>
              <DialogActions data-popup-footer>
                <Button disabled={editContentSaving} onClick={() => setEditContent(null)}>Cancel</Button>
                <Button disabled={editContentSaving || !editContent?.title?.trim()} onClick={async () => {
                  setEditContentSaving(true);
                  setEditContentError("");
                  try {
                    await mutateContentLibrary(editContent.content_type_key, [editContent.id], "update", { title: editContent.title.trim(), description: editContent.description, owner_name: editContent.owner_name || "", source: editContent.source || "", related_entity_label: editContent.related_entity_label || "", tags: [...new Set((editContent.tags || "").split(",").map((tag) => tag.trim()).filter(Boolean))] });
                    await loadContentLibrary();
                    setEditContent(null);
                    setDetailRow(null);
                    showSnackbar("Content saved.");
                  } catch (error) {
                    setEditContentError(error.message || "Content could not be saved.");
                  } finally {
                    setEditContentSaving(false);
                  }
                }}>{editContentSaving ? "Saving…" : "Save"}</Button>
              </DialogActions>
            </Dialog>

            <AddJobDialog
              open={isAddJobOpen}
              onClose={() => setIsAddJobOpen(false)}
              onCreated={() => {
                loadJobPostings();
                setActiveTab("job_postings");
                setContentStatFilterId(null);
                setIsAddJobOpen(false);
                showSnackbar("New job added to Content.");
              }}
              user={user}
            />

            <AddToSequenceDialog
              open={isAddToSequenceOpen}
              onClose={() => {
                setIsAddToSequenceOpen(false);
                setEditSequence(null);
                setSequenceDialogEntityType("job");
              }}
              entityType={editSequence?.entity_type || sequenceDialogEntityType}
              items={selectedSequenceItems}
              user={user}
              editSequence={editSequence}
              onCreated={() => {
                setSelectedIds([]);
                setIsAddToSequenceOpen(false);
                setEditSequence(null);
                setSequenceDialogEntityType("job");
                loadSequences();
                showSnackbar(
                  editSequence
                    ? "Sequence updated."
                    : isSequencesTab
                      ? "Sequence created."
                      : "Selected content items added to a sequence."
                );
              }}
            />

            <Dialog open={addNoteOpen} onClose={closeAddNoteDialog} fullWidth maxWidth="sm">
              <DialogTitle data-popup-header sx={{ fontSize: 18, fontWeight: 700 }}>
                Add Note
              </DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  {addNoteError ? <Alert severity="error">{addNoteError}</Alert> : null}
                  <TextField
                    label="Content"
                    value={addNoteTarget ? getRowPrimaryLabel(addNoteTarget) : ""}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Title"
                    value={addNoteTitle}
                    onChange={(event) => setAddNoteTitle(event.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Note"
                    value={addNoteBody}
                    onChange={(event) => setAddNoteBody(event.target.value)}
                    size="small"
                    multiline
                    minRows={5}
                    fullWidth
                  />
                </Stack>
              </DialogContent>
              <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
                <Button
                  onClick={closeAddNoteDialog}
                  disabled={addNoteSaving}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: "999px",
                    color: PURPLE,
                    border: "none !important",
                    borderColor: "transparent !important",
                    boxShadow: "none !important",
                    background: "transparent !important",
                    backgroundColor: "transparent !important",
                    "&:hover, &.Mui-focusVisible, &:active": {
                      color: "#7C3AED",
                      background: `${SOFT_PURPLE_HOVER_BG} !important`,
                      backgroundColor: `${SOFT_PURPLE_HOVER_BG} !important`,
                      border: "none !important",
                      borderColor: "transparent !important",
                      boxShadow: "none !important",
                    },
                    "&.Mui-disabled": {
                      color: "#B3B8C5",
                      border: "none !important",
                      borderColor: "transparent !important",
                      boxShadow: "none !important",
                      background: "transparent !important",
                      backgroundColor: "transparent !important",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveAddNote}
                  disabled={addNoteSaving}
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    backgroundColor: `${PURPLE} !important`,
                    background: `${PURPLE} !important`,
                    border: "none !important",
                    borderColor: "transparent !important",
                    boxShadow: "none !important",
                    "&:hover, &.Mui-focusVisible, &:active": {
                      backgroundColor: `${PURPLE} !important`,
                      background: `${PURPLE} !important`,
                      border: "none !important",
                      borderColor: "transparent !important",
                      boxShadow: "none !important",
                    },
                  }}
                >
                  {addNoteSaving ? "Saving..." : "Save Note"}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={addTagOpen} onClose={closeAddTagDialog} fullWidth maxWidth="sm">
              <DialogTitle data-popup-header sx={{ fontSize: 18, fontWeight: 700 }}>
                Add Tags
              </DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  {addTagError ? <Alert severity="error">{addTagError}</Alert> : null}
                  <TextField
                    label="Content"
                    value={addTagTarget ? getRowPrimaryLabel(addTagTarget) : ""}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Tags"
                    value={addTagValue}
                    onChange={(event) => setAddTagValue(event.target.value)}
                    size="small"
                    fullWidth
                    helperText="Separate tags with commas."
                  />
                </Stack>
              </DialogContent>
              <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
                <Button
                  onClick={closeAddTagDialog}
                  disabled={addTagSaving}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: "999px",
                    color: PURPLE,
                    border: "none !important",
                    borderColor: "transparent !important",
                    boxShadow: "none !important",
                    background: "transparent !important",
                    backgroundColor: "transparent !important",
                    "&:hover, &.Mui-focusVisible, &:active": {
                      color: "#7C3AED",
                      background: `${SOFT_PURPLE_HOVER_BG} !important`,
                      backgroundColor: `${SOFT_PURPLE_HOVER_BG} !important`,
                      border: "none !important",
                      borderColor: "transparent !important",
                      boxShadow: "none !important",
                    },
                    "&.Mui-disabled": {
                      color: "#B3B8C5",
                      border: "none !important",
                      borderColor: "transparent !important",
                      boxShadow: "none !important",
                      background: "transparent !important",
                      backgroundColor: "transparent !important",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveAddTags}
                  disabled={addTagSaving}
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    backgroundColor: `${PURPLE} !important`,
                    background: `${PURPLE} !important`,
                    border: "none !important",
                    borderColor: "transparent !important",
                    boxShadow: "none !important",
                    "&:hover, &.Mui-focusVisible, &:active": {
                      backgroundColor: `${PURPLE} !important`,
                      background: `${PURPLE} !important`,
                      border: "none !important",
                      borderColor: "transparent !important",
                      boxShadow: "none !important",
                    },
                  }}
                >
                  {addTagSaving ? "Saving..." : "Save Tags"}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        }
      />

      <CsvImportDialog
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        entityLabel="content records"
        importEndpoint={activeTab === "job_postings"
          ? `${API_BASE}/api/csv-import/jobs`
          : activeTab === "sequences" ? `${API_BASE}/api/csv-import/sequences`
          : `${API_BASE}/api/csv-import/content?type=${encodeURIComponent(activeContentTypeKey)}`}
        onImported={() => {
          if (activeTab === "sequences") return loadSequences();
          if (activeTab === "job_postings") return loadJobPostings();
          return loadContentLibrary();
        }}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3200}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
